/**
 * VersonaUniverseModule — the UNIVERSAL versona debug window (admin).
 *
 * A Debug-menu button ("Versonas → Open the Versona Universe") opens a three-
 * pane window over EVERY versona in the Commons:
 *   left   — the full roster (search + job / settlement / state filters),
 *   centre — the selected versona's 3D body (the same cooked `avatar:*` prefab
 *            the crowd renderer picks for it, lit on a studio stage),
 *   right  — "Now" (state, activity, goal, encounter), "Where" (position,
 *            distance from you, home settlement, teleport-to / bring-here),
 *            "Relationships" (family, bonds, favors, grudges — by name, click to
 *            jump), then the full inspector dossier (stats, affect, grudges,
 *            memories with edit/forget/add, intent, interactions, scenarios) —
 *            the VersonaDebugModule renders it, so the Alt+Click window and this
 *            one never drift.
 *
 * DATA LAW (probe-proven 2026-08-21): this window holds ZERO subscriptions on the
 * game connection. Roster-wide data is a periodic SQL SNAPSHOT over the HTTP
 * endpoint (`POST /v1/database/<db>/sql`) and the selected versona's live rows are
 * a 2.5 s SQL poll — on a loaded tab every new subscription on the connection
 * (which already carries ~75 AOI cell queries the server re-evaluates per sim
 * transaction) cost seconds to minutes, and applying 20k rows through the SDK
 * runs every module's per-row `onInsert` listener. The inspector renders from
 * the poll through a row source.
 * Anyone with the Commons admin cap may open it; every mutation goes through the
 * admin-gated debug reducers (the client only renders and calls).
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { STDB_WS_URI, STDB_DB_NAME, TOKEN_KEY } from '../config.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { capsIn } from '../lib/world_caps.js';
import { firstNameOf } from '../lib/versona_names.js';
import { resolvedCityName } from '../lib/city_names.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { shownAvatarFor } from '../lib/versona_bodies.js';
import { SERIES, DEFAULT_SERIES, WINDOWS, computeSeries, defaultBins, drawChart, formatValue, windowRange, type ChartEvent, type ComputedSeries, type WindowDef } from '../lib/versona_charts.js';
import { computeMetrics, METRIC_CATS, DRILL_CAP, type Drill, type MetricCat, type MetricGroup, type MetricsInput, type MSociety } from '../lib/versona_metrics.js';
import { computeWorldMetrics, emptyWorldInput, type WorldMetricsInput } from '../lib/versona_metrics_world.js';
import { computeMindMetrics, emptyMindInput, type MindMetricsInput } from '../lib/versona_metrics_mind.js';
import { computeWatch, type WatchItem } from '../lib/versona_watch.js';
import { compareCivs, emptyCivSide, type CivSide } from '../lib/versona_compare.js';
import { titleOf } from '../lib/professions.js';
import { isServiceKey } from '../lib/service_posts.js';
import type { AssetModule } from './AssetModule.js';
import type { VersonaDebugModule, DebugReducers } from './VersonaDebugModule.js';
import type { AtlasDebugMenuModule } from './AtlasDebugMenuModule.js';
import { themeColor, onThemeChange } from '../lib/ui_theme.js';

// ── snapshot rows (SQL → camelCase objects; u64 ids arrive as JSON numbers → kept as strings) ──
interface SnapVersona { id: string; archetype: string; state: string; curX: number; curZ: number; homeCityId: string }
interface SnapAgent { id: string; settlementId: string; job: string; hunger: number; needRest: number; needSocial: number; mood: number; wallet: number }
interface SnapPair { aId: string; bId: string; familiarity: number }
interface SnapFavor { aId: string; bId: string; balance: number }
interface SnapGrudge { versonaId: string; targetId: string; intensity: number; causeText: string }
interface SnapLineage { childId: string; parentA: string; parentB: string; generation: number }
interface Snapshot {
  at: number;
  versona: Map<string, SnapVersona>;
  agent: Map<string, SnapAgent>;
  civ: Map<string, string>;          // settlementId → 'a' | 'b'
  origin: Map<string, string>;       // versonaId → the civ they were BORN into (names)
  surname: Map<string, string>;      // versonaId → household surname
  econ: Map<string, { cx: number; cz: number }>;
  pairs: SnapPair[];
  favors: SnapFavor[];
  grudges: SnapGrudge[];
  lineage: SnapLineage[];
  hh: Map<string, number>;          // householdId → members (the Metrics tab)
  bornAt: Map<string, number>;      // versonaId → versona_origin.bornAt (ms; the life stage)
  relationsAt: number;              // performance.now() of the last bond fetch (0 = not loaded yet)
}
// ── live (scoped) rows read from the client cache ──
interface StateRow { versonaId: bigint; curX: number; curZ: number; state: string }
interface IntentRow { versonaId: bigint; activity: string; commitment: number }
interface GoalRow { versonaId: bigint; x: number; z: number; state: string }
interface EncounterRow { aId: bigint; bId: bigint; kind: string; state: string }
interface PrefabRow { prefabKey: string; assetId: bigint }

interface UniverseReducers {
  debugTeleportToVersona(a: { versonaId: bigint }): Promise<void>;
  setVersonaGoal(a: { versonaId: bigint; x: number; z: number; state: string; ttlSeconds: number }): Promise<void>;
}

const SNAP_REFRESH_MS = 60_000;         // the roster snapshot (2026-09-18: was 15 s — ~40k rows re-parsed on the main thread)
const RELATIONS_REFRESH_MS = 60_000;    // the bond tables, fetched on demand (a selection, the Metrics tab)
const ROSTER_PAGE = 100;                // rows rendered per page; the rest load as the list scrolls
const PANE_REFRESH_MS = 1000;
const LIVE_REFRESH_MS = 2500;
const PREVIEW_START_DELAY_MS = 1500;
const PREVIEW_SPIN_MS = 3000;
const PREVIEW_FPS = 20;
const CHART_REFRESH_MS = 60_000;
const METRICS_REFRESH_MS = 60_000;
/** The feed is a tail, not a stream: the endpoint has no ORDER BY, so each read sorts ~73k projected rows here. */
const FEED_REFRESH_MS = 15_000;
const FEED_ROWS = 200;
/** What the map can shade a settlement by. */
const MAP_METRICS: ReadonlyArray<[string, string]> = [
  ['population', 'people living there'], ['treasury', 'town treasury'], ['score', 'town score (mean of seven)'],
  ['crime', 'crime cases'], ['crimeOpen', 'crime cases still open'], ['graves', 'graves'], ['grievance', 'worst grievance held'],
];
/** A SQL Timestamp product (converted to `{ microsSinceUnixEpoch }` by sqlConverter) → epoch ms. */
const tsMs = (v: unknown): number => Number((v as { microsSinceUnixEpoch?: bigint } | null)?.microsSinceUnixEpoch ?? 0n) / 1000;

const SNAP_QUERIES: Record<string, string> = {
  // PROJECTED columns (2026-09-18): `SELECT *` on versona shipped behavior_tree + schedule_json for 10k rows on
  // every refresh — the window "just about crashed" on open. Only what the roster and the panes read.
  versona: 'SELECT versona_id, world_id, archetype, state, cur_x, cur_z, home_city_id FROM versona',
  // THE HOT ROW IS THE TRUTH (2026-09-19): `versona.state` / cur_x / cur_z are the COLD columns the AI tick stopped
  // writing — reading them is why this window said 78 % of the world stood idle. The mover writes versona_state.
  versona_state: 'SELECT versona_id, cur_x, cur_z, state FROM versona_state',
  npc_agent: 'SELECT versona_id, settlement_id, job, hunger, need_rest, need_social, mood, wallet FROM npc_agent',
  settlement_civ: 'SELECT * FROM settlement_civ',
  versona_origin: 'SELECT versona_id, civ, born_at FROM versona_origin',
  settlement_econ: 'SELECT settlement_id, center_x, center_z, mayor_id FROM settlement_econ',
  household: 'SELECT household_id, surname FROM household',
  household_member: 'SELECT household_id, versona_id FROM household_member',
};
/** The bond tables (~50k rows): fetched by refreshRelations() when a versona is selected or the Metrics tab opens — never on open. */
const RELATION_QUERIES: Record<string, string> = {
  versona_pair: 'SELECT a_id, b_id, familiarity FROM versona_pair',
  versona_favor: 'SELECT a_id, b_id, balance FROM versona_favor',
  versona_grudge: 'SELECT versona_id, target_id, intensity, cause_text FROM versona_grudge',
  versona_lineage: 'SELECT child_id, parent_a, parent_b, generation FROM versona_lineage',
};

const camel = (s: string): string => s.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
/**
 * SQL JSON → the shapes the client cache uses: U64/I64/U128 → bigint, a Timestamp product
 * (`[micros]`) → `{ microsSinceUnixEpoch }`, everything else as-is. The inspector compares ids
 * as bigints and reads `elicitedAt.microsSinceUnixEpoch`, so the poll must hand it the same types.
 */
function sqlConverter(type: unknown): (v: unknown) => unknown {
  const t = (type ?? {}) as Record<string, unknown>;
  if ('U64' in t || 'I64' in t || 'U128' in t || 'I128' in t) return (v) => (v === null || v === undefined ? v : BigInt(v as number | string));
  const prod = (t.Product as { elements?: Array<{ name?: { some?: string } }> } | undefined)?.elements;
  if (prod && prod[0]?.name?.some === '__timestamp_micros_since_unix_epoch__') return (v) => ({ microsSinceUnixEpoch: BigInt(Array.isArray(v) ? (v[0] as number | string) : (v as number | string)) });
  return (v) => v;
}
const str = (v: unknown): string => (typeof v === 'bigint' ? v.toString() : typeof v === 'number' ? String(v) : String(v ?? ''));
const num = (v: unknown): number => (typeof v === 'number' ? v : typeof v === 'bigint' ? Number(v) : Number(v ?? 0));

export class VersonaUniverseModule implements IModule {
  readonly name = 'VersonaUniverse';

  private win: UiWindow | null = null;
  private list: HTMLDivElement | null = null;
  private count: HTMLDivElement | null = null;
  private search: HTMLInputElement | null = null;
  private jobSel: HTMLSelectElement | null = null;
  private siteSel: HTMLSelectElement | null = null;
  private stateSel: HTMLSelectElement | null = null;
  private nowPane: HTMLDivElement | null = null;
  private dossier: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private caption: HTMLDivElement | null = null;
  private selected: bigint | null = null;
  private snapTimer: number | null = null;
  private paneTimer: number | null = null;
  private snap: Snapshot | null = null;
  private snapLoading = false;
  private snapError = '';
  private rosterCache: SnapVersona[] = [];
  private rosterKey = new Map<string, string>();   // id → the lowercase search key, computed once per snapshot
  private rosterVersion = 0;
  private rosterSelectsVersion = -1;
  private rosterShown = ROSTER_PAGE;
  private rosterShownCount = 0;
  private rosterMatched = 0;
  private siteNameCache = new Map<string, string>();
  private relationsLoading = false;
  private sites: SettlementSite[] = [];
  private sitesSeed = '';
  private liveTimer: number | null = null;

  // Charts tab
  private tabName: 'versonas' | 'metrics' | 'charts' | 'feed' | 'map' | 'architecture' = 'versonas';
  /** Canvases painted in an older theme (gaps wave 2026-09-28): repainted when next visible. */
  private readonly themeStale = new Set<'map' | 'charts'>();
  private paneArch: HTMLDivElement | null = null;
  private paneVersonas: HTMLDivElement | null = null;
  private paneCharts: HTMLDivElement | null = null;
  private paneMetrics: HTMLDivElement | null = null;
  private paneFeed: HTMLDivElement | null = null;
  private paneMap: HTMLDivElement | null = null;
  private metricsBox: HTMLDivElement | null = null;
  private metricsStatus: HTMLDivElement | null = null;
  private metricsTimer: number | null = null;
  private metricsLoadedAt = 0;
  private metricsLoading = false;
  private metricsError = '';
  private metricsGroups: MetricGroup[] = [];
  private metricsSourceCounts: Record<string, number> = {};
  private tabButtons = new Map<string, HTMLButtonElement>();
  private chartCanvas: HTMLCanvasElement | null = null;
  private chartLegend: HTMLDivElement | null = null;
  private chartStatus: HTMLDivElement | null = null;
  private chartSeriesBox: HTMLDivElement | null = null;
  private chartWindowSel: HTMLSelectElement | null = null;
  private chartSiteSel: HTMLSelectElement | null = null;
  private chartCommoditySel: HTMLSelectElement | null = null;
  private chartModeSel: HTMLSelectElement | null = null;
  private chartEvents: ChartEvent[] = [];
  private chartLoadedAt = 0;
  private chartLoading = false;
  private chartError = '';
  private chartSourceCounts: Record<string, number> = {};
  private chartVisible = new Set<string>(DEFAULT_SERIES);
  private chartComputed: ComputedSeries[] = [];
  private chartRange = { t0: 0, t1: 0 };
  private chartHover: number | null = null;
  private chartTimer: number | null = null;

  // 3D preview
  private canvas: HTMLCanvasElement | null = null;
  private pvRenderer: THREE.WebGPURenderer | null = null;
  private pvScene: THREE.Scene | null = null;
  private pvCamera: THREE.PerspectiveCamera | null = null;
  private pvBody: THREE.Object3D | null = null;
  private pvKey = '';
  private pvRaf = 0;
  private pvTimer = 0;
  private pvAssetId: bigint | null = null;
  private pvStarting = false;
  private pvSpinUntil = 0;
  private pvHover = false;

  constructor(private readonly assets: AssetModule, private readonly inspector: VersonaDebugModule) {}

  init(app: App): void {
    // gaps wave (2026-09-28): the map and the chart are canvases painted through themeColor, and nothing listened for a
    // theme change — they kept the old theme's ground and text until their next data refresh. Repaint the visible one
    // now, a hidden one when it is next shown.
    onThemeChange(() => { this.themeStale.add('map'); this.themeStale.add('charts'); this.repaintThemed(); });
    app.getModule<AtlasDebugMenuModule>('DebugMenu')?.registerSection('Versonas', (body) => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:8px;align-items:center;flex-wrap:wrap';
      const open = document.createElement('button');
      open.id = 'debug-open-versona-universe';
      open.textContent = 'Open the Versona Universe';
      open.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:3px 10px';
      const hint = document.createElement('span');
      hint.style.cssText = 'font-size:11px;color:var(--ax-muted)';
      hint.textContent = 'every versona in the Commons — body, stats, activity, relationships, location, memory (admin)';
      open.onclick = (): void => { if (!this.open()) hint.textContent = 'Commons admin capability required'; };
      row.append(open, hint);
      body.appendChild(row);
    });
    // Probe surface (verify:versona-universe:probe).
    (window as unknown as Record<string, unknown>).__versonaUniverse = {
      open: () => this.open(),
      close: () => this.win?.setOpen(false),
      isOpen: () => !!this.win?.isOpen(),
      ids: () => this.roster().map((v) => v.id),
      visible: () => [...(this.list?.querySelectorAll('[data-versona-id]') ?? [])].map((e) => (e as HTMLElement).dataset.versonaId),
      select: (id: string) => { this.select(BigInt(id)); return this.selected !== null && String(this.selected) === id; },
      selected: () => (this.selected === null ? null : String(this.selected)),
      filter: (text: string) => { if (this.search) { this.search.value = text; this.resetPage(); } return this.list?.querySelectorAll('[data-versona-id]').length ?? 0; }, // a new filter starts at page one, like typing
      preview: () => ({ key: this.pvKey, hasBody: !!this.pvBody, ready: !!this.pvRenderer }),
      refresh: () => { void this.refreshSnapshot(); this.renderPane(); },
      tab: (name: 'versonas' | 'metrics' | 'charts' | 'feed' | 'map' | 'architecture') => { this.showTab(name); return this.tabName; },
      metrics: () => ({
        tab: this.tabName, loaded: this.metricsLoadedAt > 0, loading: this.metricsLoading, error: this.metricsError, sources: this.metricsSourceCounts,
        world: this.worldSourceCounts,
        cat: this.metricsCat,
        cats: [{ id: 'all', label: 'All', n: this.metricsGroups.length }, ...METRIC_CATS.map((c) => ({ id: c.id, label: c.label, n: this.metricsGroups.filter((g) => g.cat === c.id).length }))],
        groups: this.metricsGroups.map((g) => ({ id: g.id, cat: g.cat, title: g.title, metrics: g.metrics, bars: g.bars ?? [], columns: g.columns ?? null, pairs: g.pairs ?? [] })),
        cards: this.metricsBox?.querySelectorAll('[data-metric-group]').length ?? 0,
      }),
      metricsReload: () => { void this.loadMetricsData(true); },
      /** Light a category chip (or 'all') and re-render the grid; returns what is lit. */
      metricsCat: (cat: MetricCat | 'all') => { this.metricsCat = cat; this.renderMetrics(); return this.metricsCat; },
      /** The watch strip: what is wrong, worst first. */
      watch: () => this.watch.map((w) => ({ id: w.id, level: w.level, cat: w.cat, text: w.text })),
      /** The deltas the last compute produced (only for values that are a plain number on both sides). */
      deltas: () => {
        const out: Array<{ key: string; was: string; now: string }> = [];
        for (const [k, v] of this.curValues) { const was = this.prevValues.get(k); if (was !== undefined && was !== v) out.push({ key: k, was, now: v }); }
        return out;
      },
      /** Open a metric's drill-down by card id + label; returns what it holds. */
      drill: (groupId: string, label: string) => {
        const m = this.metricsGroups.find((g) => g.id === groupId)?.metrics.find((x) => x.label === label);
        if (!m?.drill) return null;
        this.openDrill(m.drill);
        return { kind: m.drill.kind, label: m.drill.label, ids: m.drill.ids.length, more: m.drill.more ?? 0, rendered: document.querySelectorAll('#vu-drill [data-drill-id]').length };
      },
      closeDrill: () => { this.drillPanel?.remove(); this.drillPanel = null; },
      /** Every metric that carries a door, so a probe can prove the drills are wired. */
      drills: () => this.metricsGroups.flatMap((g) => g.metrics.filter((m) => m.drill).map((m) => ({ group: g.id, label: m.label, n: m.drill!.ids.length }))),
      feed: () => ({
        loaded: this.feedLoadedAt > 0, loading: this.feedLoading, rows: this.feedRows.length,
        kinds: [...new Set(this.feedRows.map((r) => r.kind))].length, kind: this.feedKind,
        shown: document.querySelectorAll('#vu-feed-list [data-feed-kind]').length,
        newest: this.feedRows[0] ? { kind: this.feedRows[0].kind, ageS: Math.round((Date.now() - this.feedRows[0].at) / 1000) } : null,
        ordered: this.feedRows.every((r, i) => i === 0 || this.feedRows[i - 1].at >= r.at),
      }),
      feedKind: (k: string) => { this.feedKind = k; if (this.feedKindSel) this.feedKindSel.value = k; this.renderFeed(); return document.querySelectorAll('#vu-feed-list [data-feed-kind]').length; },
      map: () => ({
        loaded: this.mapLoadedAt > 0, points: this.mapPoints.length, metric: this.mapMetric,
        metrics: MAP_METRICS.map((m) => m[0]),
        drawn: (() => {
          const c = this.mapCanvas; if (!c || !c.width) return false;
          const g = c.getContext('2d'); if (!g) return false;
          const d = g.getImageData(0, 0, c.width, c.height).data;
          let ink = 0;
          for (let i = 0; i < d.length; i += 4 * 97) if (d[i] > 40 || d[i + 1] > 40 || d[i + 2] > 60) ink++;
          return ink > 20;
        })(),
        top: [...this.mapPoints].sort((a, b) => (b.vals[this.mapMetric] ?? 0) - (a.vals[this.mapMetric] ?? 0)).slice(0, 3).map((p) => ({ name: p.name, v: p.vals[this.mapMetric] ?? 0 })),
      }),
      mapMetric: (k: string) => { this.mapMetric = k; if (this.mapSel) this.mapSel.value = k; this.paintMap(); return this.mapMetric; },
      roster: () => ({ shown: this.rosterShownCount, matched: this.rosterMatched, total: this.rosterCache.length, page: ROSTER_PAGE }),
      loadMore: () => { this.loadMore(); return this.rosterShownCount; },
      relations: () => ({ loaded: (this.snap?.relationsAt ?? 0) > 0, loading: this.relationsLoading, pairs: this.snap?.pairs.length ?? 0, grudges: this.snap?.grudges.length ?? 0 }),
      loadRelations: () => this.refreshRelations(true),
      charts: () => ({
        tab: this.tabName, loaded: this.chartLoadedAt > 0, loading: this.chartLoading, error: this.chartError, sources: this.chartSourceCounts,
        events: this.chartEvents.length, window: this.chartWindowSel?.value ?? null, mode: this.chartModeSel?.value ?? null,
        visible: [...this.chartVisible], bins: this.chartComputed[0]?.values.length ?? 0,
        series: this.chartComputed.map((c) => ({ id: c.id, first: c.first, last: c.last, min: c.min, max: c.max, deltaPct: c.deltaPct, points: c.values.filter((v) => v !== null).length })),
        drawn: this.chartCanvasHasInk(),
      }),
      chartsToggle: (id: string, on: boolean) => { if (on) this.chartVisible.add(id); else this.chartVisible.delete(id); this.renderChartControls(); this.renderCharts(); return [...this.chartVisible]; },
      chartsWindow: (label: string) => { if (this.chartWindowSel) { this.chartWindowSel.value = label; this.renderCharts(); } return this.chartWindowSel?.value ?? null; },
      chartsMode: (mode: string) => { if (this.chartModeSel) { this.chartModeSel.value = mode; this.renderCharts(); } return this.chartModeSel?.value ?? null; },
      chartsReload: () => { void this.loadChartData(true); },
      counts: () => ({
        versona: this.snap?.versona.size ?? 0, npcAgent: this.snap?.agent.size ?? 0, pairs: this.snap?.pairs.length ?? 0,
        snapshotAgeS: this.snap ? Math.round((performance.now() - this.snap.at) / 1000) : null, snapError: this.snapError,
        live: { for: this.liveFor, ageS: this.liveAt ? Math.round((performance.now() - this.liveAt) / 1000) : null, versonaState: this.liveRows('versonaState').length, versonaMemory: this.liveRows('versonaMemory').length, npcMood: this.liveRows('npcMood').length },
      }),
    };
  }

  // ─── gate + data ─────────────────────────────────────────────

  private isAdminHere(): boolean {
    return atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId && capsIn(atlas.currentWorldId).admin;
  }

  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  /**
   * Reducers over HTTP (`POST /v1/database/<db>/call/<reducer>`, named JSON args, the same
   * bearer identity) — the window's writes never queue behind the game connection, whose
   * sends lagged minutes on a loaded tab (a teleport click took >3 min to reach the server).
   * bigint args become JSON numbers (ids and coin are far below 2^53).
   */
  private http: UniverseReducers & DebugReducers = new Proxy({}, {
    get: (_t, name: string) => (args: Record<string, unknown>) => this.callHttp(name, args),
  }) as unknown as UniverseReducers & DebugReducers;

  private async callHttp(reducer: string, args: Record<string, unknown>): Promise<void> {
    const base = STDB_WS_URI.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
    const token = (atlas.conn as unknown as { token?: string } | null)?.token ?? localStorage.getItem(TOKEN_KEY) ?? '';
    const snake = reducer.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    const body = JSON.stringify(args, (_k, v) => (typeof v === 'bigint' ? Number(v) : v));
    const res = await fetch(`${base}/v1/database/${STDB_DB_NAME}/call/${snake}`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body });
    if (!res.ok) throw new Error((await res.text()).slice(0, 200) || `${reducer}: HTTP ${res.status}`);
  }

  private reducers(): UniverseReducers { return this.http; }

  /** One-shot SQL over the HTTP endpoint (same host as the WebSocket, scheme swapped). */
  private async sql(query: string): Promise<Array<Record<string, unknown>>> {
    const base = STDB_WS_URI.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
    const token = (atlas.conn as unknown as { token?: string } | null)?.token ?? localStorage.getItem(TOKEN_KEY) ?? '';
    const res = await fetch(`${base}/v1/database/${STDB_DB_NAME}/sql`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/plain' }, body: query });
    if (!res.ok) throw new Error(`sql ${res.status}: ${(await res.text()).slice(0, 120)}`);
    const out = (await res.json()) as Array<{ schema: { elements: Array<{ name: { some?: string }; algebraicType?: unknown; algebraic_type?: unknown }> }; rows: unknown[][] }>;
    for (const st of out) for (const e of st.schema.elements) if (e.algebraicType === undefined) e.algebraicType = e.algebraic_type;
    const stmt = out[0];
    if (!stmt) return [];
    const cols = stmt.schema.elements.map((e) => camel(e.name.some ?? ''));
    const conv = stmt.schema.elements.map((e) => sqlConverter(e.algebraicType));
    return stmt.rows.map((r) => { const o: Record<string, unknown> = {}; cols.forEach((c, i) => { o[c] = conv[i](r[i]); }); return o; });
  }

  private async refreshSnapshot(): Promise<void> {
    if (this.snapLoading) return;
    this.snapLoading = true;
    try {
      const names = Object.keys(SNAP_QUERIES);
      const results = await Promise.all(names.map((n) => this.sql(SNAP_QUERIES[n])));
      const t: Record<string, Array<Record<string, unknown>>> = {};
      names.forEach((n, i) => { t[n] = results[i]; });
      const wid = str(atlas.commonsWorldId);
      const prev = this.snap;
      const snap: Snapshot = {
        at: performance.now(), versona: new Map(), agent: new Map(), civ: new Map(), origin: new Map(), surname: new Map(), econ: new Map(),
        pairs: prev?.pairs ?? [], favors: prev?.favors ?? [], grudges: prev?.grudges ?? [], lineage: prev?.lineage ?? [], hh: new Map(), bornAt: new Map(), relationsAt: prev?.relationsAt ?? 0,
      };
      for (const r of t.versona) if (str(r.worldId) === wid) snap.versona.set(str(r.versonaId), { id: str(r.versonaId), archetype: str(r.archetype), state: str(r.state), curX: num(r.curX), curZ: num(r.curZ), homeCityId: str(r.homeCityId) });
      for (const r of t.versona_state ?? []) {
        const v = snap.versona.get(str(r.versonaId));
        if (v) { v.state = str(r.state); v.curX = num(r.curX); v.curZ = num(r.curZ); }
      }
      this.mayors = new Map();
      for (const r of t.settlement_econ) if (str(r.mayorId) !== '0') this.mayors.set(str(r.settlementId), str(r.mayorId));
      for (const r of t.npc_agent) snap.agent.set(str(r.versonaId), { id: str(r.versonaId), settlementId: str(r.settlementId), job: str(r.job), hunger: num(r.hunger), needRest: num(r.needRest), needSocial: num(r.needSocial), mood: num(r.mood), wallet: num(r.wallet) });
      for (const r of t.settlement_civ) snap.civ.set(str(r.settlementId), str(r.civ));
      for (const r of t.versona_origin) { snap.origin.set(str(r.versonaId), str(r.civ)); snap.bornAt.set(str(r.versonaId), tsMs(r.bornAt)); }
      for (const r of t.settlement_econ) snap.econ.set(str(r.settlementId), { cx: num(r.centerX), cz: num(r.centerZ) });
      const households = new Map<string, string>();
      for (const r of t.household) households.set(str(r.householdId), str(r.surname));
      for (const r of t.household_member) { const hid = str(r.householdId); const sn = households.get(hid); if (sn) snap.surname.set(str(r.versonaId), sn); snap.hh.set(hid, (snap.hh.get(hid) ?? 0) + 1); }
      this.snap = snap;
      this.snapError = '';
      // THE ROSTER KEY IS COMPUTED ONCE (2026-09-18): the search called label() per versona per keystroke and
      // the list re-rendered a DOM row for every one of 10k — see renderList's paging.
      const keyed = [...snap.versona.values()].map((v) => ({ v, n: this.label(v.id).toLowerCase() })).sort((a, b) => (a.n < b.n ? -1 : a.n > b.n ? 1 : 0));
      this.rosterCache = keyed.map((x) => x.v);
      this.rosterKey = new Map(keyed.map((x) => [x.v.id, `${x.n} ${x.v.archetype.toLowerCase()} #${x.v.id}`]));
      this.siteNameCache.clear();
      this.rosterVersion++;
      this.renderList();
    } catch (e) {
      this.snapError = String((e as Error)?.message ?? e);
      if (this.count) this.count.textContent = `snapshot failed: ${this.snapError}`;
    } finally {
      this.snapLoading = false;
    }
  }

  /** The bond tables (pairs / favors / grudges / lineage) — ON DEMAND, merged into the roster snapshot; `force` ignores the age. */
  async refreshRelations(force = false): Promise<void> {
    if (this.relationsLoading) { while (this.relationsLoading) await new Promise((r) => setTimeout(r, 200)); if (!force) return; }
    if (!force && this.snap && this.snap.relationsAt > 0 && performance.now() - this.snap.relationsAt < RELATIONS_REFRESH_MS) return;
    this.relationsLoading = true;
    try {
      const names = Object.keys(RELATION_QUERIES);
      const results = await Promise.all(names.map((n) => this.sql(RELATION_QUERIES[n])));
      const t: Record<string, Array<Record<string, unknown>>> = {};
      names.forEach((n, i) => { t[n] = results[i]; });
      for (let i = 0; !this.snap && i < 60; i++) await new Promise((r) => setTimeout(r, 250)); // the roster lands first
      const snap = this.snap;
      if (!snap) return;
      snap.pairs = t.versona_pair.map((r) => ({ aId: str(r.aId), bId: str(r.bId), familiarity: num(r.familiarity) }));
      snap.favors = t.versona_favor.map((r) => ({ aId: str(r.aId), bId: str(r.bId), balance: num(r.balance) }));
      snap.grudges = t.versona_grudge.map((r) => ({ versonaId: str(r.versonaId), targetId: str(r.targetId), intensity: num(r.intensity), causeText: str(r.causeText) }));
      snap.lineage = t.versona_lineage.map((r) => ({ childId: str(r.childId), parentA: str(r.parentA), parentB: str(r.parentB), generation: num(r.generation) }));
      snap.relationsAt = performance.now();
      this.renderPane();
    } catch (e) {
      this.snapError = String((e as Error)?.message ?? e);
    } finally {
      this.relationsLoading = false;
    }
  }

    private roster(): SnapVersona[] { return this.rosterCache; }

  /** "Bjorild (smith #9483)" — the Lab's label shape, from the snapshot (no table scans). */
  private firstName(id: string): string {
    const a = this.snap?.agent.get(id);
    const civ = this.snap?.origin.get(id) || (a && this.snap?.civ.get(a.settlementId)) || 'a'; // birth civ first
    return firstNameOf(BigInt(id), civ);
  }
  private fullName(id: string): string { const s = this.snap?.surname.get(id); return s ? `${this.firstName(id)} ${s}` : this.firstName(id); }
  private label(id: string): string { const a = this.snap?.agent.get(id); const v = this.snap?.versona.get(id); return `${this.firstName(id)} (${a?.job ?? v?.archetype ?? '?'} #${id})`; }
  private fullLabel(id: string): string { const a = this.snap?.agent.get(id); return `${this.fullName(id)}, ${a?.job ?? 'settler'} (#${id})`; }

  private siteList(): SettlementSite[] {
    const seed = this.tbl<{ worldId: bigint; terrainSeed: string }>('world').find((w) => w.worldId === atlas.commonsWorldId)?.terrainSeed ?? '';
    if (seed && seed !== this.sitesSeed) { this.sites = settlementSites(seed); this.sitesSeed = seed; }
    return this.sites;
  }
  /** DB settlementId = site.id + 1 (the world-map law). */
  private siteOf(settlementId: string): SettlementSite | null { return this.siteList().find((s) => String(s.id + 1) === settlementId) ?? null; }
  private settlementName(settlementId: string): string {
    const hit = this.siteNameCache.get(settlementId);
    if (hit !== undefined) return hit;
    const site = this.siteOf(settlementId);
    const name = site ? resolvedCityName(site, atlas.conn) : `settlement ${settlementId}`; // resolvedCityName walks the settlement_name table — once per site per snapshot now
    this.siteNameCache.set(settlementId, name);
    return name;
  }

  /** Live position from the selected versona's poll, else the snapshot's cold position. */
  private whereIs(id: string): { x: number; z: number; hot: boolean } | null {
    const hot = (this.live.get('versonaState') as StateRow[] | undefined)?.find((s) => String(s.versonaId) === id);
    if (hot) return { x: hot.curX, z: hot.curZ, hot: true };
    const cold = this.snap?.versona.get(id);
    return cold ? { x: cold.curX, z: cold.curZ, hot: false } : null;
  }

  /**
   * The selected versona's LIVE rows, polled over SQL every LIVE_REFRESH_MS — the window
   * holds ZERO subscriptions on the game connection (each new one cost seconds there;
   * see the module header). Keys are the client-cache table names the inspector reads.
   */
  private live = new Map<string, unknown[]>();
  private liveAt = 0;
  private liveFor = '';
  private liveLoading = false;
  private async refreshLive(): Promise<void> {
    const id = this.selected === null ? null : String(this.selected);
    if (id === null || this.liveLoading) return;
    this.liveLoading = true;
    const sid = this.snap?.agent.get(id)?.settlementId;
    const plan: Array<[string, string[]]> = [
      ['versona', [`SELECT * FROM versona WHERE versona_id = ${id}`]],
      ['npcAgent', [`SELECT * FROM npc_agent WHERE versona_id = ${id}`]],
      ['versonaState', [`SELECT * FROM versona_state WHERE versona_id = ${id}`]],
      ['versonaIntent', [`SELECT * FROM versona_intent WHERE versona_id = ${id}`]],
      ['versonaGoal', [`SELECT * FROM versona_goal WHERE versona_id = ${id}`]],
      ['npcMind', [`SELECT * FROM npc_mind WHERE versona_id = ${id}`]],
      ['npcMood', [`SELECT * FROM npc_mood WHERE versona_id = ${id}`]],
      ['versonaEmotion', [`SELECT * FROM versona_emotion WHERE versona_id = ${id}`]],
      ['versonaGrudge', [`SELECT * FROM versona_grudge WHERE versona_id = ${id}`]],
      ['versonaMemory', [`SELECT * FROM versona_memory WHERE versona_id = ${id}`]],
      ['versonaBelief', [`SELECT * FROM versona_belief WHERE versona_id = ${id}`]],
      ['versonaThought', [`SELECT * FROM versona_thought WHERE versona_id = ${id}`]],
      ['versonaAspiration', [`SELECT * FROM versona_aspiration WHERE versona_id = ${id}`]],
      ['versonaChat', [`SELECT * FROM versona_chat WHERE versona_id = ${id}`]],
      ['npcPantry', [`SELECT * FROM npc_pantry WHERE versona_id = ${id}`]],
      ['faithAdherence', [`SELECT * FROM faith_adherence WHERE versona_id = ${id}`]],
      ['versonaDeliberation', [`SELECT * FROM versona_deliberation WHERE versona_id = ${id}`]],
      ['versonaOpinion', [`SELECT * FROM versona_opinion WHERE versona_id = ${id}`]],
      ['versonaPair', [`SELECT * FROM versona_pair WHERE a_id = ${id}`, `SELECT * FROM versona_pair WHERE b_id = ${id}`]],
      ['versonaFavor', [`SELECT * FROM versona_favor WHERE a_id = ${id}`, `SELECT * FROM versona_favor WHERE b_id = ${id}`]],
      ['versonaEncounter', [`SELECT * FROM versona_encounter WHERE a_id = ${id}`, `SELECT * FROM versona_encounter WHERE b_id = ${id}`]],
      ['versonaLineage', [`SELECT * FROM versona_lineage WHERE child_id = ${id}`, `SELECT * FROM versona_lineage WHERE parent_a = ${id}`, `SELECT * FROM versona_lineage WHERE parent_b = ${id}`]],
      ['npcEvent', [`SELECT * FROM npc_event WHERE versona_id = ${id}`, ...(sid ? [`SELECT * FROM npc_event WHERE settlement_id = ${sid}`] : [])]],
      ['settlementCiv', sid ? [`SELECT * FROM settlement_civ WHERE settlement_id = ${sid}`] : []],
    ];
    try {
      const results = await Promise.all(plan.map(async ([table, qs]) => { const parts = await Promise.all(qs.map((q) => this.sql(q))); return [table, parts.flat()] as const; }));
      if (String(this.selected) !== id) return; // selection moved on while polling
      const next = new Map<string, unknown[]>();
      for (const [table, rows] of results) next.set(table, rows);
      this.live = next; this.liveAt = performance.now(); this.liveFor = id;
      this.renderPane();
    } catch (e) {
      this.setNote(`live poll failed: ${String((e as Error).message ?? e)}`, true);
    } finally { this.liveLoading = false; }
  }
  private liveRows(name: string): unknown[] { return this.liveFor === (this.selected === null ? '' : String(this.selected)) ? this.live.get(name) ?? [] : []; }

  // ─── window ──────────────────────────────────────────────────

  /** Open (admin in the Commons only). Returns false when refused. */
  open(): boolean {
    if (!this.isAdminHere()) return false;
    if (!this.win) this.build();
    this.win!.setOpen(true);
    void this.refreshSnapshot();
    this.renderPane();
    this.repaintThemed();
    if (this.snapTimer === null) this.snapTimer = window.setInterval(() => { if (this.win?.isOpen()) void this.refreshSnapshot(); }, SNAP_REFRESH_MS);
    if (this.paneTimer === null) this.paneTimer = window.setInterval(() => { if (this.win?.isOpen()) this.renderPane(); }, PANE_REFRESH_MS);
    if (this.liveTimer === null) this.liveTimer = window.setInterval(() => { if (this.win?.isOpen()) void this.refreshLive(); }, LIVE_REFRESH_MS);
    if (this.previewStartTimer !== null) window.clearTimeout(this.previewStartTimer); // (bug sweep) one per open(), never cleared
    this.previewStartTimer = window.setTimeout(() => { this.previewStartTimer = null; void this.startPreview(); }, PREVIEW_START_DELAY_MS);
    return true;
  }

  private build(): void {
    this.win = makeWindow({
      id: 'versona-universe', title: 'Versona Universe (admin)', icon: 'logic',
      width: 1180, height: 720, minWidth: 860, minHeight: 480, x: 40, y: 40,
      onClose: () => this.pausePreview(),
    });
    const shell = document.createElement('div');
    shell.style.cssText = 'display:flex;flex-direction:column;height:100%;color:var(--ax-text);font:12px/1.5 var(--ax-font);box-sizing:border-box';
    const tabs = document.createElement('div');
    tabs.id = 'vu-tabs';
    tabs.style.cssText = 'display:flex;gap:4px;padding:4px 8px;border-bottom:1px solid var(--ax-line);background:var(--ax-bg2)';
    for (const [name, label] of [['versonas', 'Versonas'], ['metrics', 'Metrics'], ['charts', 'Charts'], ['feed', 'Feed'], ['map', 'Map'], ['architecture', 'Architecture']] as const) {
      const b = document.createElement('button');
      b.id = `vu-tab-${name}`;
      b.textContent = label;
      b.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 12px;font:12px var(--ax-font)';
      b.onclick = (): void => this.showTab(name);
      this.tabButtons.set(name, b);
      tabs.appendChild(b);
    }
    shell.appendChild(tabs);
    const root = document.createElement('div');
    root.style.cssText = 'display:flex;flex:1;min-height:0;color:var(--ax-text);font:12px/1.5 var(--ax-font);box-sizing:border-box';
    this.paneVersonas = root;
    // ── left: roster ──
    const left = document.createElement('div');
    left.style.cssText = 'width:280px;display:flex;flex-direction:column;border-right:1px solid var(--ax-line);min-width:220px';
    const tools = document.createElement('div');
    tools.style.cssText = 'display:flex;flex-direction:column;gap:4px;padding:6px 8px;border-bottom:1px solid var(--ax-line)';
    this.search = document.createElement('input');
    this.search.placeholder = 'search name · job · #id';
    this.search.id = 'vu-search';
    this.search.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:3px 6px';
    this.search.oninput = (): void => this.resetPage();
    const sels = document.createElement('div');
    sels.style.cssText = 'display:flex;gap:4px';
    const mk = (title: string): HTMLSelectElement => {
      const s = document.createElement('select');
      s.title = title;
      s.style.cssText = 'flex:1;min-width:0;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:2px';
      s.onchange = (): void => this.resetPage();
      return s;
    };
    this.jobSel = mk('job'); this.siteSel = mk('settlement'); this.stateSel = mk('state');
    sels.append(this.jobSel, this.siteSel, this.stateSel);
    this.count = document.createElement('div');
    this.count.style.cssText = 'font-size:11px;color:var(--ax-soft)';
    tools.append(this.search, sels, this.count);
    this.list = document.createElement('div');
    this.list.id = 'vu-list';
    this.list.style.cssText = 'flex:1;overflow:auto;padding:4px 0';
    left.append(tools, this.list);
    // ── centre: body ──
    const centre = document.createElement('div');
    centre.style.cssText = 'width:320px;display:flex;flex-direction:column;align-items:center;border-right:1px solid var(--ax-line);background:radial-gradient(ellipse at 50% 30%, var(--ax-bg3) 0%, var(--ax-bg) 70%)';
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'vu-preview';
    this.canvas.style.cssText = 'width:320px;height:440px;display:block';
    this.caption = document.createElement('div');
    this.caption.style.cssText = 'padding:6px 10px;text-align:center;color:var(--ax-text);font-size:12px;min-height:36px;white-space:pre-line';
    centre.append(this.canvas, this.caption);
    // ── right: now + relationships + dossier ──
    const right = document.createElement('div');
    right.style.cssText = 'flex:1;display:flex;flex-direction:column;min-width:0';
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;align-items:center;padding:6px 8px;border-bottom:1px solid var(--ax-line)';
    this.note = document.createElement('div');
    this.note.style.cssText = 'flex:1;text-align:right;opacity:.85';
    bar.appendChild(this.note);
    const scroll = document.createElement('div');
    scroll.style.cssText = 'flex:1;overflow:auto;padding:8px 10px';
    this.nowPane = document.createElement('div');
    this.nowPane.id = 'vu-now';
    this.dossier = document.createElement('div');
    this.dossier.id = 'vu-dossier';
    scroll.append(this.nowPane, this.dossier);
    right.append(bar, scroll);
    root.append(left, centre, right);
    shell.appendChild(root);
    this.paneMetrics = this.buildMetrics();
    shell.appendChild(this.paneMetrics);
    this.paneCharts = this.buildCharts();
    shell.appendChild(this.paneCharts);
    this.paneFeed = this.buildFeed();
    shell.appendChild(this.paneFeed);
    this.paneMap = this.buildMap();
    shell.appendChild(this.paneMap);
    this.paneArch = this.buildArch();
    shell.appendChild(this.paneArch);
    this.win.body.appendChild(shell);
    this.showTab('versonas');
  }

  // ─── tabs ────────────────────────────────────────────────────

  private showTab(name: 'versonas' | 'metrics' | 'charts' | 'feed' | 'map' | 'architecture'): void {
    this.tabName = name;
    if (this.paneVersonas) this.paneVersonas.style.display = name === 'versonas' ? 'flex' : 'none';
    if (this.paneMetrics) this.paneMetrics.style.display = name === 'metrics' ? 'flex' : 'none';
    if (name === 'metrics') {
      void this.loadMetricsData(false);
      if (this.metricsTimer === null) this.metricsTimer = window.setInterval(() => { if (this.win?.isOpen() && this.tabName === 'metrics') void this.loadMetricsData(false); }, METRICS_REFRESH_MS);
    }
    if (this.paneCharts) this.paneCharts.style.display = name === 'charts' ? 'flex' : 'none';
    if (this.paneFeed) this.paneFeed.style.display = name === 'feed' ? 'flex' : 'none';
    if (this.paneMap) this.paneMap.style.display = name === 'map' ? 'flex' : 'none';
    if (name === 'feed') {
      void this.loadFeed(false);
      if (this.feedTimer === null) this.feedTimer = window.setInterval(() => { if (this.win?.isOpen() && this.tabName === 'feed') void this.loadFeed(false); }, FEED_REFRESH_MS);
    }
    if (name === 'map') { void this.loadMapData(false); }
    if (this.paneArch) this.paneArch.style.display = name === 'architecture' ? 'flex' : 'none';
    if (name === 'architecture') this.renderArch();
    for (const [n, b] of this.tabButtons) { b.style.background = n === name ? 'var(--ax-sel)' : 'var(--ax-bg3)'; b.style.color = n === name ? 'var(--ax-text)' : 'var(--ax-hi)'; }
    if (name === 'charts') {
      void this.loadChartData(false);
      if (this.chartTimer === null) this.chartTimer = window.setInterval(() => { if (this.win?.isOpen() && this.tabName === 'charts') void this.loadChartData(false); }, CHART_REFRESH_MS);
      this.renderCharts();
    }
    this.repaintThemed();
  }

  /** Repaint the visible canvas tab if it was painted in an older theme. */
  private repaintThemed(): void {
    if (!this.win?.isOpen()) return;
    if (this.tabName === 'map' && this.themeStale.has('map')) this.paintMap();
    if (this.tabName === 'charts' && this.themeStale.has('charts')) this.paintChart();
  }

  // ─── FEED: what just happened (2026-09-20) ──────────────────────────────
  //
  // The Charts tab aggregates history into series; the Metrics tab reduces the
  // present into numbers. Neither shows you the world SPEAKING. This is the
  // raw tail of `npc_event` and `chronicle`, newest first.
  //
  // The SQL endpoint supports no ORDER BY and no timestamp literal, so there is
  // no "give me the newest 80" — the tail is taken by fetching the projected
  // columns (10.9k + 62k rows, ~1.8 MB) and sorting here. That is why it is its
  // own tab on a slow timer rather than a panel on the Metrics grid.
  private feedRows: Array<{ at: number; src: string; kind: string; sid: string; vid: string; detail: string }> = [];
  private feedBox: HTMLDivElement | null = null;
  private feedStatus: HTMLDivElement | null = null;
  private feedKindSel: HTMLSelectElement | null = null;
  private feedTimer: number | null = null;
  private feedLoadedAt = 0;
  private feedLoading = false;
  private feedKind = '';

  private buildFeed(): HTMLDivElement {
    const pane = document.createElement('div');
    pane.id = 'vu-feed';
    pane.style.cssText = 'display:none;flex:1;min-height:0;flex-direction:column';
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:10px;align-items:center;padding:6px 10px;border-bottom:1px solid var(--ax-line);font-size:11px;color:var(--ax-soft);flex-wrap:wrap';
    const reload = document.createElement('button');
    reload.id = 'vu-feed-reload';
    reload.textContent = 'Refresh';
    reload.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 8px;font:11px var(--ax-font)';
    reload.onclick = (): void => { void this.loadFeed(true); };
    this.feedKindSel = document.createElement('select');
    this.feedKindSel.id = 'vu-feed-kind';
    this.feedKindSel.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 4px;font:11px var(--ax-font)';
    this.feedKindSel.onchange = (): void => { this.feedKind = this.feedKindSel?.value ?? ''; this.renderFeed(); };
    this.feedStatus = document.createElement('div');
    this.feedStatus.id = 'vu-feed-status';
    bar.append(reload, this.feedKindSel, this.feedStatus);
    this.feedBox = document.createElement('div');
    this.feedBox.id = 'vu-feed-list';
    this.feedBox.style.cssText = 'flex:1;overflow:auto;padding:6px 10px;display:flex;flex-direction:column;gap:2px';
    pane.append(bar, this.feedBox);
    return pane;
  }

  private async loadFeed(force: boolean): Promise<void> {
    if (this.feedLoading) return;
    if (!force && this.feedLoadedAt > 0 && performance.now() - this.feedLoadedAt < FEED_REFRESH_MS) return;
    this.feedLoading = true;
    if (this.feedStatus) this.feedStatus.textContent = 'reading…';
    try {
      const q = async (sql: string): Promise<Array<Record<string, unknown>>> => { try { return await this.sql(sql); } catch { return []; } };
      const [evs, chron] = await Promise.all([
        q('SELECT kind, settlement_id, versona_id, detail, at FROM npc_event'),
        q('SELECT kind, settlement_id, actor_id, detail, at FROM chronicle'),
      ]);
      const rows = [
        ...evs.map((r) => ({ at: tsMs(r.at), src: 'npc', kind: str(r.kind), sid: str(r.settlementId), vid: str(r.versonaId), detail: str(r.detail) })),
        ...chron.map((r) => ({ at: tsMs(r.at), src: 'chronicle', kind: str(r.kind), sid: str(r.settlementId), vid: str(r.actorId), detail: str(r.detail) })),
      ];
      rows.sort((x, y) => y.at - x.at);
      this.feedRows = rows.slice(0, FEED_ROWS);
      // Rebuild the kind filter from what actually came back, keeping the choice.
      if (this.feedKindSel) {
        const kinds = [...new Set(this.feedRows.map((r) => r.kind))].sort();
        const keep = this.feedKindSel.value;
        this.feedKindSel.textContent = '';
        for (const [v, l] of [['', `every kind (${kinds.length})`], ...kinds.map((k) => [k, k] as [string, string])]) {
          const o = document.createElement('option'); o.value = v; o.textContent = l; this.feedKindSel.appendChild(o);
        }
        this.feedKindSel.value = kinds.includes(keep) ? keep : '';
        this.feedKind = this.feedKindSel.value;
      }
      this.feedLoadedAt = performance.now();
      if (this.feedStatus) this.feedStatus.textContent = `${evs.length.toLocaleString('en-US')} npc events · ${chron.length.toLocaleString('en-US')} chronicle entries · newest ${FEED_ROWS}`;
      this.renderFeed();
    } catch (e) {
      if (this.feedStatus) this.feedStatus.textContent = `feed failed: ${String((e as Error)?.message ?? e)}`;
    } finally {
      this.feedLoading = false;
    }
  }

  private renderFeed(): void {
    const box = this.feedBox;
    if (!box) return;
    box.textContent = '';
    const now = Date.now();
    const ago = (ms: number): string => {
      const s2 = Math.max(0, (now - ms) / 1000);
      return s2 < 90 ? `${s2.toFixed(0)} s` : s2 < 5400 ? `${(s2 / 60).toFixed(0)} min` : s2 < 172800 ? `${(s2 / 3600).toFixed(0)} h` : `${(s2 / 86400).toFixed(0)} d`;
    };
    for (const r of this.feedRows) {
      if (this.feedKind && r.kind !== this.feedKind) continue;
      const row = document.createElement('div');
      row.dataset.feedKind = r.kind;
      row.style.cssText = 'display:grid;grid-template-columns:52px 96px 1fr;gap:8px;align-items:baseline;font-size:11.5px;padding:1px 0;border-bottom:1px solid var(--ax-line)';
      const t = document.createElement('span'); t.textContent = ago(r.at); t.style.cssText = 'color:var(--ax-muted);text-align:right;font-variant-numeric:tabular-nums';
      const k = document.createElement('span'); k.textContent = r.kind;
      k.style.cssText = `color:${r.src === 'chronicle' ? 'var(--ax-rare)' : 'var(--ax-hi)'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis`;
      k.title = r.src === 'chronicle' ? 'a chronicle entry — the town remembers this one' : 'an npc_event — the world doing something';
      const d = document.createElement('span');
      d.textContent = r.detail || (r.vid && r.vid !== '0' ? this.label(r.vid) : `settlement #${r.sid}`);
      d.style.cssText = 'color:var(--ax-text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
      if (r.vid && r.vid !== '0') {
        d.style.cursor = 'pointer';
        d.onclick = (): void => { this.showTab('versonas'); this.select(BigInt(r.vid)); };
      }
      row.append(t, k, d);
      box.appendChild(row);
    }
    if (box.children.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'nothing of that kind in the tail';
      empty.style.cssText = 'color:var(--ax-muted);font-size:11.5px;padding:8px 0';
      box.appendChild(empty);
    }
  }

  // ─── MAP: the world, shaded by any number (2026-09-20) ──────────────────
  //
  // Every other view is a list. A world with 315 settlements has SHAPE — where
  // the crime is, where the money is, which quarter is starving — and a table
  // sorted by settlement id hides all of it.
  private mapPoints: Array<{ sid: string; x: number; z: number; name: string; vals: Record<string, number> }> = [];
  private mapCanvas: HTMLCanvasElement | null = null;
  private mapSel: HTMLSelectElement | null = null;
  private mapStatus: HTMLDivElement | null = null;
  private mapMetric = 'population';
  private mapLoadedAt = 0;
  private mapLoading = false;

  private buildMap(): HTMLDivElement {
    const pane = document.createElement('div');
    pane.id = 'vu-map';
    pane.style.cssText = 'display:none;flex:1;min-height:0;flex-direction:column';
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:10px;align-items:center;padding:6px 10px;border-bottom:1px solid var(--ax-line);font-size:11px;color:var(--ax-soft);flex-wrap:wrap';
    this.mapSel = document.createElement('select');
    this.mapSel.id = 'vu-map-metric';
    this.mapSel.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 4px;font:11px var(--ax-font)';
    for (const [v, l] of MAP_METRICS) { const o = document.createElement('option'); o.value = v; o.textContent = l; this.mapSel.appendChild(o); }
    this.mapSel.onchange = (): void => { this.mapMetric = this.mapSel?.value ?? 'population'; this.paintMap(); };
    const reload = document.createElement('button');
    reload.id = 'vu-map-reload';
    reload.textContent = 'Refresh';
    reload.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 8px;font:11px var(--ax-font)';
    reload.onclick = (): void => { void this.loadMapData(true); };
    this.mapStatus = document.createElement('div');
    this.mapStatus.id = 'vu-map-status';
    bar.append(reload, this.mapSel, this.mapStatus);
    this.mapCanvas = document.createElement('canvas');
    this.mapCanvas.id = 'vu-map-canvas';
    this.mapCanvas.style.cssText = 'flex:1;min-height:0;width:100%;display:block';
    pane.append(bar, this.mapCanvas);
    return pane;
  }

  private async loadMapData(force: boolean): Promise<void> {
    if (this.mapLoading) return;
    if (!force && this.mapLoadedAt > 0 && performance.now() - this.mapLoadedAt < METRICS_REFRESH_MS) return;
    this.mapLoading = true;
    if (this.mapStatus) this.mapStatus.textContent = 'reading…';
    try {
      if (!this.snap) await this.refreshSnapshot();
      const q = async (sql: string): Promise<Array<Record<string, unknown>>> => { try { return await this.sql(sql); } catch { return []; } };
      const [econ, scores, crimes, graves, rivalries] = await Promise.all([
        q('SELECT settlement_id, center_x, center_z, treasury FROM settlement_econ'),
        q('SELECT settlement_id, cohesion, readiness, learning, health, order, piety, prosperity FROM settlement_score'),
        q('SELECT settlement_id, status FROM crime_case'),
        q('SELECT settlement_id FROM grave'),
        q('SELECT a_id, b_id, grievance FROM settlement_rivalry'),
      ]);
      const pop = new Map<string, number>();
      for (const a of this.snap?.agent.values() ?? []) pop.set(a.settlementId, (pop.get(a.settlementId) ?? 0) + 1);
      const crimeN = new Map<string, number>(), openN = new Map<string, number>();
      for (const c of crimes) {
        const sid = str(c.settlementId);
        crimeN.set(sid, (crimeN.get(sid) ?? 0) + 1);
        if (str(c.status) === 'open') openN.set(sid, (openN.get(sid) ?? 0) + 1);
      }
      const graveN = new Map<string, number>();
      for (const g of graves) { const sid = str(g.settlementId); graveN.set(sid, (graveN.get(sid) ?? 0) + 1); }
      const hot = new Map<string, number>();
      for (const r of rivalries) {
        const g = num(r.grievance);
        for (const sid of [str(r.aId), str(r.bId)]) hot.set(sid, Math.max(hot.get(sid) ?? 0, g));
      }
      const scoreOf = new Map<string, number>();
      for (const sc of scores) {
        scoreOf.set(str(sc.settlementId), (num(sc.cohesion) + num(sc.readiness) + num(sc.learning) + num(sc.health) + num(sc.order) + num(sc.piety) + num(sc.prosperity)) / 7);
      }
      this.mapPoints = econ.map((r) => {
        const sid = str(r.settlementId);
        return {
          sid, x: num(r.centerX), z: num(r.centerZ), name: this.settlementName(sid),
          vals: {
            population: pop.get(sid) ?? 0,
            treasury: num(r.treasury),
            score: scoreOf.get(sid) ?? 0,
            crime: crimeN.get(sid) ?? 0,
            crimeOpen: openN.get(sid) ?? 0,
            graves: graveN.get(sid) ?? 0,
            grievance: hot.get(sid) ?? 0,
          },
        };
      });
      this.mapLoadedAt = performance.now();
      if (this.mapStatus) this.mapStatus.textContent = `${this.mapPoints.length} settlements`;
      this.paintMap();
    } catch (e) {
      if (this.mapStatus) this.mapStatus.textContent = `map failed: ${String((e as Error)?.message ?? e)}`;
    } finally {
      this.mapLoading = false;
    }
  }

  /** Blue (low) → amber → red (high). A settlement with nothing stays dark. */
  private static heat(t: number): string {
    const c = Math.max(0, Math.min(1, t));
    const r = Math.round(40 + c * 215), g = Math.round(90 + Math.sin(c * Math.PI) * 120 - c * 60), b = Math.round(190 - c * 170);
    return `rgb(${r},${Math.max(0, g)},${Math.max(0, b)})`;
  }

  private paintMap(): void {
    const c = this.mapCanvas;
    if (!c || this.mapPoints.length === 0) return;
    this.themeStale.delete('map');
    const w = Math.max(320, Math.floor(c.clientWidth || 900)), h = Math.max(240, Math.floor(c.clientHeight || 560));
    c.width = w; c.height = h;
    const g = c.getContext('2d');
    if (!g) return;
    g.fillStyle = themeColor('--ax-bg'); g.fillRect(0, 0, w, h); // canvas chrome reads the theme (THE TOKEN LAW)
    const key = this.mapMetric;
    const xs = this.mapPoints.map((p) => p.x), zs = this.mapPoints.map((p) => p.z);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
    const pad = 26;
    // ONE scale for both axes, or the continent shears into an ellipse.
    const sc = Math.min((w - pad * 2) / Math.max(1, maxX - minX), (h - pad * 2) / Math.max(1, maxZ - minZ));
    const ox = (w - (maxX - minX) * sc) / 2, oz = (h - (maxZ - minZ) * sc) / 2;
    const vals = this.mapPoints.map((p) => p.vals[key] ?? 0);
    const hi = Math.max(1, ...vals);
    for (const p of this.mapPoints) {
      const v = p.vals[key] ?? 0;
      const x = ox + (p.x - minX) * sc, y = oz + (p.z - minZ) * sc;
      const t = v / hi;
      g.beginPath();
      g.arc(x, y, 2.5 + Math.sqrt(t) * 9, 0, Math.PI * 2);
      g.fillStyle = v === 0 ? themeColor('--ax-bg3') : VersonaUniverseModule.heat(t);
      g.globalAlpha = v === 0 ? 0.55 : 0.85;
      g.fill();
    }
    g.globalAlpha = 1;
    // The legend, and the three biggest named so the picture has anchors.
    g.font = `11px ${themeColor('--ax-font')}`;
    g.fillStyle = themeColor('--ax-muted');
    const label = MAP_METRICS.find((m) => m[0] === key)?.[1] ?? key;
    g.fillText(`${label} · 0 … ${hi.toLocaleString('en-US')}`, 10, 16);
    const top = [...this.mapPoints].sort((a, b) => (b.vals[key] ?? 0) - (a.vals[key] ?? 0)).slice(0, 3);
    g.fillStyle = themeColor('--ax-text');
    for (const [i, p] of top.entries()) {
      const x = ox + (p.x - minX) * sc, y = oz + (p.z - minZ) * sc;
      g.fillText(`${p.name} ${(p.vals[key] ?? 0).toLocaleString('en-US')}`, Math.min(w - 120, x + 8), y - 6 - i * 0);
    }
  }

  // ─── metrics tab (2026-09-18: "make sure the versona universe window is up to date with its metrics") ───

  private buildMetrics(): HTMLDivElement {
    const pane = document.createElement('div');
    pane.id = 'vu-metrics';
    pane.style.cssText = 'display:none;flex:1;min-height:0;flex-direction:column';
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:10px;align-items:center;padding:6px 10px;border-bottom:1px solid var(--ax-line);font-size:11px;color:var(--ax-soft);flex-wrap:wrap';
    const reload = document.createElement('button');
    reload.id = 'vu-metrics-reload';
    reload.textContent = 'Recompute';
    reload.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 8px;font:11px var(--ax-font)';
    reload.onclick = (): void => { void this.loadMetricsData(true); };
    this.metricsStatus = document.createElement('div');
    this.metricsStatus.id = 'vu-metrics-status';
    this.metricsStatus.style.cssText = 'min-height:14px';
    const note = document.createElement('span');
    note.style.cssText = 'color:var(--ax-muted)';
    note.textContent = 'computed from the SQL snapshot plus the live government / space / guild / market / PvP / crafting tables — refreshed every minute while this tab is open';
    // CATEGORY CHIPS (2026-09-19): the tab grew from 14 cards to ~26 when
    // government, space, guilds, economy, PvP and crafting landed, and one flat
    // grid stopped being readable. The chips filter the SAME grid — no second
    // tab strip, no second load path, and "All" still gives the whole sweep.
    const chips = document.createElement('div');
    chips.id = 'vu-metrics-cats';
    chips.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap';
    for (const c of [{ id: 'all' as const, label: 'All' }, ...METRIC_CATS]) {
      const b = document.createElement('button');
      b.dataset.cat = c.id;
      b.dataset.label = c.label;
      b.textContent = c.label;
      b.onclick = (): void => { this.metricsCat = c.id; this.renderMetrics(); };
      chips.appendChild(b);
    }
    this.metricsChips = chips;
    bar.append(reload, chips, this.metricsStatus, note);
    // EXPORT (2026-09-20): a tab you can only read is a tab you cannot take to
    // anyone else. JSON keeps the structure; CSV opens in a spreadsheet.
    const mkBtn = (id: string, label: string, onClick: () => void): HTMLButtonElement => {
      const b2 = document.createElement('button');
      b2.id = id; b2.textContent = label;
      b2.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 8px;font:11px var(--ax-font)';
      b2.onclick = onClick;
      return b2;
    };
    bar.append(mkBtn('vu-metrics-json', 'JSON', () => this.exportMetrics('json')), mkBtn('vu-metrics-csv', 'CSV', () => this.exportMetrics('csv')));
    // THE WATCH STRIP sits ABOVE the grid and ignores the chips: an alert you
    // have to go and find is not an alert.
    this.watchBox = document.createElement('div');
    this.watchBox.id = 'vu-metrics-watch';
    this.watchBox.style.cssText = 'display:none;flex-direction:column;gap:3px;padding:6px 10px;border-bottom:1px solid var(--ax-line)';
    this.metricsBox = document.createElement('div');
    this.metricsBox.id = 'vu-metrics-grid';
    this.metricsBox.style.cssText = 'flex:1;overflow:auto;padding:10px;display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:10px;align-content:start';
    pane.append(bar, this.watchBox, this.metricsBox);
    return pane;
  }

  /** Which category chip is lit; 'all' shows every card. */
  private metricsCat: MetricCat | 'all' = 'all';
  private metricsChips: HTMLDivElement | null = null;

  /** Paint the chips: the lit one, and the card count each would show. */
  private paintChips(): void {
    if (!this.metricsChips) return;
    for (const el of [...this.metricsChips.children] as HTMLButtonElement[]) {
      const id = el.dataset.cat ?? 'all';
      const n = id === 'all' ? this.metricsGroups.length : this.metricsGroups.filter((g) => g.cat === id).length;
      const on = id === this.metricsCat;
      el.disabled = n === 0 && id !== 'all';
      el.textContent = `${el.dataset.label ?? ''} \u00b7 ${n}`;   // the base label is kept in the dataset, never re-parsed out of the text
      el.style.cssText = `cursor:${el.disabled ? 'default' : 'pointer'};background:${on ? 'var(--ax-sel)' : 'var(--ax-bg3)'};color:${el.disabled ? 'var(--ax-muted)' : on ? 'var(--ax-text)' : 'var(--ax-hi)'};border:1px solid ${on ? 'var(--ax-muted)' : 'var(--ax-line)'};border-radius:var(--ax-win-radius);padding:2px 9px;font:11px var(--ax-font)`;
    }
  }

  /** settlementId → mayorId (from the snapshot; the Metrics tab counts the mayor as an office). */
  private mayors = new Map<string, string>();

  /** The wave's rows → the pure metrics input: titles (post > practised craft > base job), posts, scores, federations. */
  private societyInput(
    snap: Snapshot, slots: Array<Record<string, unknown>>, skills: Array<Record<string, unknown>>, scores: Array<Record<string, unknown>>,
    fstate: Array<Record<string, unknown>>, fmember: Array<Record<string, unknown>>, fbill: Array<Record<string, unknown>>,
    fwork: Array<Record<string, unknown>>, fcase: Array<Record<string, unknown>>, made: Array<Record<string, unknown>>,
    stations: number, tradeOrders: number, marketLinks: number,
  ): MSociety {
    const postOf = new Map<string, string>();
    const posts = new Map<string, { key: string; held: number; open: number }>();
    for (const s of slots) {
      const key = str(s.recipeKey);
      if (!isServiceKey(key)) continue;
      let p = posts.get(key);
      if (!p) { p = { key, held: 0, open: 0 }; posts.set(key, p); }
      if (str(s.holderId) === '0') p.open++; else { p.held++; postOf.set(str(s.holderId), key); }
    }
    const skillOf = new Map<string, Array<{ recipeKey: string; level: number }>>();
    for (const k of skills) {
      const id = str(k.versonaId);
      let list = skillOf.get(id);
      if (!list) { list = []; skillOf.set(id, list); }
      list.push({ recipeKey: str(k.recipeKey), level: num(k.level) });
    }
    const titles = new Map<string, string>();
    for (const a of snap.agent.values()) {
      const post = postOf.get(a.id);
      titles.set(a.id, post ? post.replace('svc:', '').replace(/_/g, ' ') : titleOf(a.job, skillOf.get(a.id) ?? []));
    }
    const count = (rows: Array<Record<string, unknown>>, civ: string, pred: (r: Record<string, unknown>) => boolean): number => rows.filter((r) => str(r.civ) === civ && pred(r)).length;
    return {
      titles, postsHeld: [...posts.values()].reduce((s, p) => s + p.held, 0), postsOpen: [...posts.values()].reduce((s, p) => s + p.open, 0),
      postsByKey: [...posts.values()],
      scores: scores.map((r) => ({ settlementId: str(r.settlementId), cohesion: num(r.cohesion), readiness: num(r.readiness), learning: num(r.learning), health: num(r.health), order: num(r.order), piety: num(r.piety), prosperity: num(r.prosperity) })),
      federations: fstate.map((r) => {
        const civ = str(r.civ);
        return {
          civ, purse: num(r.treasury), levyBps: num(r.levyBps), customsBps: num(r.customsBps), legitimacy: num(r.legitimacy), speaker: str(r.speakerName),
          atWarWith: str(r.atWarWith), reserveGrain: num(r.reserveGrain),
          members: count(fmember, civ, () => true), suspended: count(fmember, civ, (m) => m.suspended === true), defiant: count(fmember, civ, (m) => m.defiant === true),
          openBills: count(fbill, civ, (b) => str(b.status) === 'open'), passedBills: count(fbill, civ, (b) => str(b.status) === 'passed'),
          works: count(fwork, civ, (w) => str(w.status) === 'building'), worksDone: count(fwork, civ, (w) => str(w.status) === 'done'),
          casesOpen: count(fcase, civ, (c) => str(c.status) === 'proposed'), casesAccepted: count(fcase, civ, (c) => str(c.status) === 'accepted'), casesRefused: count(fcase, civ, (c) => str(c.status) === 'refused'),
        };
      }),
      madeItems: made.map((r) => ({ itemKey: str(r.itemKey) })), stations, tradeOrders, marketLinks,
    };
  }

  /** The snapshot rows + the live tables → `computeMetrics` (pure) → the cards. */
  private async loadMetricsData(force: boolean): Promise<void> {
    if (this.metricsLoading) return;
    if (!force && this.metricsLoadedAt > 0 && performance.now() - this.metricsLoadedAt < METRICS_REFRESH_MS) return;
    this.metricsLoading = true;
    this.metricsError = '';
    if (this.metricsStatus) this.metricsStatus.textContent = 'computing…';
    try {
      if (!this.snap) await this.refreshSnapshot();
      for (let i = 0; !this.snap && i < 60; i++) await new Promise((r) => setTimeout(r, 500)); // a refresh in flight elsewhere
      const snap = this.snap;
      if (!snap) throw new Error('the roster snapshot has not landed yet');
      if (!snap.relationsAt || performance.now() - snap.relationsAt > RELATIONS_REFRESH_MS) await this.refreshRelations(true); // bonds / households / lineage KPIs
      const wid = str(atlas.commonsWorldId);
      const inWorld = (rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> => rows.filter((r) => str(r.worldId) === wid);
      // SOCIETY WAVE: the wave's own tables (small, except npc_skill / job_slot which are projected).
      const [slots, skills, scores, fstate, fmember, fbill, fwork, fcase, made, stationsN, orders, links] = await Promise.all([
        this.sql('SELECT recipe_key, holder_id FROM job_slot'), this.sql('SELECT versona_id, recipe_key, level FROM npc_skill'),
        this.sql('SELECT * FROM settlement_score'), this.sql('SELECT * FROM federal_state'), this.sql('SELECT civ, suspended, defiant FROM federal_member'),
        this.sql('SELECT civ, status FROM federal_bill'), this.sql('SELECT civ, status FROM federal_work'), this.sql('SELECT civ, status FROM tribunal_case'),
        this.sql('SELECT item_key FROM versona_made_item'), this.sql('SELECT station_id FROM activity_station'),
        this.sql('SELECT order_id FROM trade_order'), this.sql('SELECT settlement_id FROM market_link'),
      ]);
      const [journeys, goals, duties, conds, air, bunits, graves, migr, offices] = await Promise.all([
        this.sql('SELECT * FROM versona_journey'), this.sql('SELECT * FROM versona_goal'), this.sql('SELECT * FROM war_assignment'),
        this.sql('SELECT * FROM unit_condition'), this.sql('SELECT * FROM war_air_unit'), this.sql('SELECT * FROM battle_unit'),
        this.sql('SELECT * FROM grave'), this.sql('SELECT * FROM migration_log'), this.sql('SELECT * FROM settlement_office'),
      ]);
      const tierOf = new Map<string, string>(this.siteList().map((s) => [String(s.id + 1), s.tier]));
      const nowMs = Date.now();
      const input: MetricsInput = {
        nowMs,
        versonas: [...snap.versona.values()],
        agents: [...snap.agent.values()],
        civOfSettlement: snap.civ,
        originCiv: snap.origin,
        tierOfSettlement: tierOf,
        lineage: snap.lineage.map((l) => ({ childId: l.childId, bornAtMs: snap.bornAt.get(l.childId) ?? nowMs, generation: l.generation })),
        pairs: snap.pairs,
        favors: snap.favors,
        grudges: snap.grudges,
        households: [...snap.hh.entries()].map(([householdId, members]) => ({ householdId, members })),
        journeys: inWorld(journeys).map((r) => ({ reason: str(r.reason), kind: str(r.kind) })),
        goals: goals.map((r) => ({ state: str(r.state) })),
        duties: inWorld(duties).map((r) => ({ kind: str(r.kind) })),
        conditions: inWorld(conds).map((r) => ({ injuryTier: num(r.injuryTier), fitAtMs: tsMs(r.fitForDutyAt), battleId: str(r.battleId) })),
        air: inWorld(air).map((r) => ({ side: str(r.side), kind: str(r.kind), state: str(r.state), hp: num(r.hp) })),
        battleUnits: inWorld(bunits).map((r) => ({ side: str(r.side), state: str(r.state), role: str(r.role), hp: num(r.hp), morale: num(r.morale) })),
        graves: inWorld(graves).map((r) => ({ cause: str(r.cause), diedAtMs: tsMs(r.diedAt), settlementId: str(r.settlementId) })),
        migrations: inWorld(migr).map((r) => ({ reason: str(r.reason), atMs: tsMs(r.at) })),
        // The MAYOR is an office too (a hamlet's is its reeve, already a settlement_office row).
        offices: [
          ...inWorld(offices).map((r) => ({ office: str(r.office), settlementId: str(r.settlementId) })),
          ...[...this.mayors.keys()].filter((sid) => tierOf.get(sid) !== 'hamlet').map((sid) => ({ office: 'mayor', settlementId: sid })),
        ],
        society: this.societyInput(snap, slots, skills, scores, fstate, fmember, fbill, fwork, fcase, made, stationsN.length, orders.length, links.length),
      };
      const world = await this.worldInput();
      const cmp = this.civSides(input, world);
      const fresh = [...computeMetrics(input), ...computeWorldMetrics(world), ...(cmp ? [cmp] : [])];
      this.rollValues(fresh);
      this.metricsGroups = [...fresh, ...this.mindGroups];
      this.lastWorld = world;
      this.prevPopulation = this.lastPopulation;
      this.lastPopulation = snap.versona.size;
      this.refreshWatch();
      this.metricsSourceCounts = {
        versona: snap.versona.size, npcAgent: snap.agent.size, pairs: snap.pairs.length, favors: snap.favors.length, grudges: snap.grudges.length, lineage: snap.lineage.length, households: snap.hh.size,
        journeys: journeys.length, goals: goals.length, duties: duties.length, conditions: conds.length, air: air.length, battleUnits: bunits.length, graves: graves.length, migrations: migr.length, offices: offices.length,
      };
      this.metricsLoadedAt = performance.now();
      this.renderMetrics();
      // PHASE TWO (2026-09-20): the cognition tables are ~6.8 MB — belief 2.0,
      // provenance 0.9, price beliefs 0.9 — so they land AFTER the first paint.
      // The people and world cards are on screen in the meantime; the mind
      // cards appear a beat later. Never awaited: a slow mind must not hold the
      // tab, and a failed one must not empty it.
      void this.loadMindData(force);
    } catch (e) {
      this.metricsError = String((e as Error)?.message ?? e);
      if (this.metricsStatus) this.metricsStatus.textContent = `metrics failed: ${this.metricsError}`;
    } finally {
      this.metricsLoading = false;
    }
  }

  /**
   * The WORLD tables behind the government / law / space / guild / economy /
   * PvP / crafting cards (2026-09-19). Every one is PROJECTED to the columns
   * the metrics actually read — the Universe's law, and it matters here:
   * charter_clause is 3.8k rows, market_round 7.3k, settlement_stock 4.0k,
   * crime_case 2.4k. One statement per request, all in flight together; a
   * table an older module has not published yet resolves to [] rather than
   * failing the whole tab.
   */
  private async worldInput(): Promise<WorldMetricsInput> {
    const w = emptyWorldInput();
    const q = async (sql: string): Promise<Array<Record<string, unknown>>> => {
      try { return await this.sql(sql); } catch { return []; }   // an absent table must not sink the tab
    };
    const [
      races, terms, seats, recalls, charter, norms, fedOffices, promises,
      treaties, goodwill, rivalries, petitions, memorials, crimes,
    ] = await Promise.all([
      q('SELECT state, winner_id FROM election_race'), q('SELECT term_id, seats, closed FROM council_term'),
      q('SELECT term_id, inf_total FROM council_seat'), q('SELECT status, signatures, votes_for, votes_against FROM recall_petition'),
      q('SELECT kind, status, yes_votes, no_votes FROM charter_clause'), q('SELECT key, active, adoption_x_10 AS adoption FROM settlement_norm'),   // ALIASED: camel() only lifts _[a-z], so adoption_x_10 would arrive as `adoptionX_10`
      q('SELECT civ, office, holder_name FROM federal_office'), q('SELECT civ, kind, status FROM federal_promise'),
      q('SELECT kind, status FROM settlement_treaty'), q('SELECT goodwill FROM settlement_goodwill'),
      q('SELECT a_id, b_id, grievance FROM settlement_rivalry'), q('SELECT state, signatures, threshold FROM war_petition'),
      q('SELECT settlement_id FROM war_memorial'), q('SELECT kind, status, confidence, accused_id, settlement_id FROM crime_case'),
    ]);
    const [
      locs, structs, sectors, ships, comps, wrecks, pilots, pskills, missions,
      firms, deposits, shipments, ports, couriers, campaigns, kills,
    ] = await Promise.all([
      q('SELECT mode, system_id FROM space_location'), q('SELECT kind FROM space_structure'),
      q('SELECT system_id, score FROM sector_control'), q('SELECT chassis_key, active FROM player_ship'),
      q('SELECT slot, quality FROM ship_component'), q('SELECT chassis_key FROM ship_wreck'),
      q('SELECT faction_key, rank, xp, tokens FROM pilot_state'), q('SELECT skill_key, level FROM pilot_skill'),
      q('SELECT kind, state, reward_sov FROM pilot_mission'),
      q('SELECT kind, hulls, hulls_target, short_hours FROM space_firm'), q('SELECT ore_key, remaining, capacity FROM space_deposit'),
      q('SELECT state, qty, value_sov FROM space_shipment'), q('SELECT state, sov_earned, sov_spent FROM planetary_port'),
      q('SELECT state, reward_sov FROM courier_contract'), q('SELECT state, winner, rounds FROM space_campaign'),
      q('SELECT attacker_kind, value_sov FROM kill_record'),
    ]);
    const [
      guilds, gmembers, gversonas, gstores, gwars, gelections,
      rounds, tradeOrders, links, stocks, coins, escrows,
      duels, precords, psectors, made, schems, forge, extractors, nodes, ticks,
    ] = await Promise.all([
      q('SELECT guild_id, name, tag FROM guild'), q('SELECT guild_id, title FROM guild_member'),
      q('SELECT guild_id, versona_id, job FROM guild_versona'), q('SELECT guild_id, key, qty FROM guild_store'),
      q('SELECT state FROM guild_war'), q('SELECT office, resolved FROM guild_election'),
      q('SELECT settlement_id, commodity, avg_price, volume, unfilled, epoch FROM market_round'),
      q('SELECT commodity, qty, premium_bps FROM trade_order'), q('SELECT trips FROM market_link'),
      q('SELECT commodity, qty FROM settlement_stock'), q('SELECT source, coins FROM coin_issuance'),
      q('SELECT state, amount_sov FROM escrow'), q('SELECT state FROM pvp_duel'),
      q('SELECT duels_won, duels_lost, duels_drawn, matches_won, matches_lost, objectives FROM pvp_record'),
      q('SELECT name, active, score_a, score_b FROM pvp_sector'),
      q('SELECT item_key, quality FROM versona_made_item'), q('SELECT schematic_key, source FROM player_schematic'),
      q('SELECT status, fidelity_tier FROM forge_project'), q('SELECT concentration, stored_qty FROM extractor_state'),
      q('SELECT good, richness FROM resource_node'), q('SELECT name, seq, last_at, gap_micros FROM sim_tick_ledger'),
    ]);
    w.races = races.map((r) => ({ state: str(r.state), winnerId: str(r.winnerId) }));
    w.councilTerms = terms.map((r) => ({ termId: str(r.termId), seats: num(r.seats), closed: !!r.closed }));
    w.councilSeats = seats.map((r) => ({ termId: str(r.termId), infTotal: num(r.infTotal) }));
    w.recalls = recalls.map((r) => ({ status: str(r.status), signatures: num(r.signatures), votesFor: num(r.votesFor), votesAgainst: num(r.votesAgainst) }));
    w.charter = charter.map((r) => ({ kind: str(r.kind), status: str(r.status), yesVotes: num(r.yesVotes), noVotes: num(r.noVotes) }));
    w.norms = norms.map((r) => ({ key: str(r.key), active: !!r.active, adoptionX10: num(r.adoption) }));
    w.fedOffices = fedOffices.map((r) => ({ civ: str(r.civ), office: str(r.office), holderName: str(r.holderName) }));
    w.promises = promises.map((r) => ({ civ: str(r.civ), kind: str(r.kind), status: str(r.status) }));
    w.treaties = treaties.map((r) => ({ kind: str(r.kind), status: str(r.status) }));
    w.goodwill = goodwill.map((r) => ({ goodwill: num(r.goodwill) }));
    w.rivalries = rivalries.map((r) => ({ aId: str(r.aId), bId: str(r.bId), grievance: num(r.grievance) }));
    w.petitions = petitions.map((r) => ({ state: str(r.state), signatures: num(r.signatures), threshold: num(r.threshold) }));
    w.memorials = memorials.map((r) => ({ settlementId: str(r.settlementId) }));
    w.crimes = crimes.map((r) => ({ kind: str(r.kind), status: str(r.status), confidence: num(r.confidence), accusedId: str(r.accusedId), settlementId: str(r.settlementId) }));
    w.spaceLocs = locs.map((r) => ({ mode: str(r.mode), systemId: str(r.systemId) }));
    w.spaceStructs = structs.map((r) => ({ kind: str(r.kind) }));
    w.sectors = sectors.map((r) => ({ systemId: str(r.systemId), score: num(r.score) }));
    w.ships = ships.map((r) => ({ chassisKey: str(r.chassisKey), active: !!r.active }));
    w.comps = comps.map((r) => ({ slot: str(r.slot), quality: num(r.quality) }));
    w.wrecks = wrecks.map((r) => ({ chassisKey: str(r.chassisKey) }));
    w.pilots = pilots.map((r) => ({ factionKey: str(r.factionKey), rank: num(r.rank), xp: num(r.xp), tokens: num(r.tokens) }));
    w.pilotSkills = pskills.map((r) => ({ skillKey: str(r.skillKey), level: num(r.level) }));
    w.missions = missions.map((r) => ({ kind: str(r.kind), state: str(r.state), rewardSov: num(r.rewardSov) }));
    w.firms = firms.map((r) => ({ kind: str(r.kind), hulls: num(r.hulls), hullsTarget: num(r.hullsTarget), shortHours: num(r.shortHours) }));
    w.deposits = deposits.map((r) => ({ oreKey: str(r.oreKey), remaining: num(r.remaining), capacity: num(r.capacity) }));
    w.shipments = shipments.map((r) => ({ state: str(r.state), qty: num(r.qty), valueSov: num(r.valueSov) }));
    w.ports = ports.map((r) => ({ state: str(r.state), sovEarned: num(r.sovEarned), sovSpent: num(r.sovSpent) }));
    w.couriers = couriers.map((r) => ({ state: str(r.state), rewardSov: num(r.rewardSov) }));
    w.campaigns = campaigns.map((r) => ({ state: str(r.state), winner: str(r.winner), rounds: num(r.rounds) }));
    w.kills = kills.map((r) => ({ attackerKind: str(r.attackerKind), valueSov: num(r.valueSov) }));
    w.guilds = guilds.map((r) => ({ guildId: str(r.guildId), name: str(r.name), tag: str(r.tag) }));
    w.guildMembers = gmembers.map((r) => ({ guildId: str(r.guildId), title: str(r.title) }));
    w.guildVersonas = gversonas.map((r) => ({ guildId: str(r.guildId), versonaId: str(r.versonaId), job: str(r.job) }));
    w.guildStores = gstores.map((r) => ({ guildId: str(r.guildId), key: str(r.key), qty: num(r.qty) }));
    w.guildWars = gwars.map((r) => ({ state: str(r.state) }));
    w.guildElections = gelections.map((r) => ({ office: str(r.office), resolved: !!r.resolved }));
    w.rounds = rounds.map((r) => ({ settlementId: str(r.settlementId), commodity: str(r.commodity), avgPrice: num(r.avgPrice), volume: num(r.volume), unfilled: num(r.unfilled), epoch: num(r.epoch) }));
    w.tradeOrders = tradeOrders.map((r) => ({ commodity: str(r.commodity), qty: num(r.qty), premiumBps: num(r.premiumBps) }));
    w.links = links.map((r) => ({ trips: num(r.trips) }));
    w.stocks = stocks.map((r) => ({ commodity: str(r.commodity), qty: num(r.qty) }));
    w.coins = coins.map((r) => ({ source: str(r.source), coins: num(r.coins) }));
    w.escrows = escrows.map((r) => ({ state: str(r.state), amountSov: num(r.amountSov) }));
    w.duels = duels.map((r) => ({ state: str(r.state) }));
    w.pvpRecords = precords.map((r) => ({ duelsWon: num(r.duelsWon), duelsLost: num(r.duelsLost), duelsDrawn: num(r.duelsDrawn), matchesWon: num(r.matchesWon), matchesLost: num(r.matchesLost), objectives: num(r.objectives) }));
    w.pvpSectors = psectors.map((r) => ({ name: str(r.name), active: !!r.active, scoreA: num(r.scoreA), scoreB: num(r.scoreB) }));
    w.made = made.map((r) => ({ itemKey: str(r.itemKey), quality: num(r.quality) }));
    w.schematics = schems.map((r) => ({ schematicKey: str(r.schematicKey), source: str(r.source) }));
    w.forge = forge.map((r) => ({ status: str(r.status), fidelityTier: str(r.fidelityTier) }));
    w.extractors = extractors.map((r) => ({ concentration: num(r.concentration), storedQty: num(r.storedQty) }));
    w.nodes = nodes.map((r) => ({ good: str(r.good), richness: num(r.richness) }));
    w.ticks = ticks.map((r) => ({ name: str(r.name), seq: num(r.seq), lastAtMs: tsMs(r.lastAt), gapMicros: num(r.gapMicros) }));
    w.nowMs = Date.now();
    this.worldSourceCounts = {
      races: races.length, charter: charter.length, crimes: crimes.length, treaties: treaties.length,
      spaceLocs: locs.length, ships: ships.length, pilots: pilots.length, firms: firms.length, kills: kills.length,
      guilds: guilds.length, guildVersonas: gversonas.length, rounds: rounds.length, stocks: stocks.length,
      duels: duels.length, pvpRecords: precords.length, made: made.length, nodes: nodes.length,
    };
    return w;
  }

  /** Row counts behind the world cards, for the probe. */
  private worldSourceCounts: Record<string, number> = {};
  /** The cognition cards, computed in a second phase (they carry the heavy tables). */
  private mindGroups: MetricGroup[] = [];
  private mindLoadedAt = 0;
  private mindLoading = false;
  private mindSourceCounts: Record<string, number> = {};
  private lastWorld: WorldMetricsInput | null = null;
  private lastMind: MindMetricsInput | null = null;
  private lastPopulation = 0;
  private prevPopulation = 0;
  /** The watch strip: what is wrong, recomputed with the cards. */
  private watch: WatchItem[] = [];
  private watchBox: HTMLDivElement | null = null;
  /**
   * WHAT CHANGED (2026-09-20). The tab was a photograph; these two maps make it
   * a monitor. `cur` is what the last compute produced, `prev` what the one
   * before it did, per `groupId|label`. They roll in the COMPUTE, never in the
   * render — clicking a chip re-renders, and a delta that reset itself every
   * time you filtered would be a lie.
   */
  private prevValues = new Map<string, string>();
  private curValues = new Map<string, string>();

  /** Roll one phase's metrics into the delta maps. Each phase owns its own keys. */
  private rollValues(groups: readonly MetricGroup[]): void {
    for (const g of groups) {
      for (const m of g.metrics) {
        const k = `${g.id}|${m.label}`;
        const c = this.curValues.get(k);
        if (c !== undefined) this.prevValues.set(k, c);
        this.curValues.set(k, m.value);
      }
    }
  }

  /** Recompute the watch strip from whatever inputs have landed so far. */
  private refreshWatch(): void {
    if (!this.lastWorld) return;
    this.watch = computeWatch({
      world: this.lastWorld, mind: this.lastMind ?? emptyMindInput(),
      population: this.lastPopulation, populationWas: this.prevPopulation,
    });
  }

  /**
   * THE MIND OF THE WORLD. ~490,000 rows of cognition that nothing had ever
   * aggregated — every one of these tables was read `WHERE versona_id = …`, one
   * versona at a time, for the dossier. Measured against the live Commons the
   * whole set is 6.8 MB and ~330 ms of server time, so it runs once per refresh
   * window behind the first paint, never on the critical path.
   *
   * `market_round` is NOT re-fetched: the world phase already has it, and the
   * price card needs it to weigh belief against truth.
   */
  private async loadMindData(force: boolean): Promise<void> {
    if (this.mindLoading) return;
    if (!force && this.mindLoadedAt > 0 && performance.now() - this.mindLoadedAt < METRICS_REFRESH_MS) return;
    this.mindLoading = true;
    try {
      const q = async (sql: string): Promise<Array<Record<string, unknown>>> => {
        try { return await this.sql(sql); } catch { return []; }
      };
      const [beliefs, prov, moods, minds, emotions, aspirations, opinions] = await Promise.all([
        q('SELECT kind, weight, confidence FROM versona_belief'),
        q('SELECT hops, confidence FROM memory_provenance'),
        // ALIASED, all six: `camel()` lifts _[a-z] only, so pleasure_x_10 would
        // arrive as `pleasureX_10` and every mood would silently read 0.
        q('SELECT pleasure_x_10 AS p, arousal_x_10 AS a, dominance_x_10 AS d, base_pleasure_x_10 AS bp, base_arousal_x_10 AS ba, base_dominance_x_10 AS bd FROM npc_mood'),
        q('SELECT joy, anger, fear, hunger_streak FROM npc_mind'),
        q('SELECT etype, intensity, half_life_s FROM versona_emotion'),
        q('SELECT kind, progress FROM versona_aspiration'),
        q('SELECT lean_x_10 AS lean FROM versona_opinion'),
      ]);
      const [intents, faiths, priceBeliefs, kin, playerRels] = await Promise.all([
        q('SELECT activity, commitment FROM versona_intent'),
        q('SELECT faith_id, intensity FROM faith_adherence'),
        q('SELECT commodity, mu, sigma FROM price_belief'),
        q('SELECT kind FROM kinship'),
        q('SELECT versona_id, nemesis, rival, seen_count, talk_streak FROM player_relation'),
      ]);
      const m: MindMetricsInput = {
        ...emptyMindInput(),
        population: this.lastPopulation,
        beliefs: beliefs.map((r) => ({ kind: str(r.kind), weight: num(r.weight), confidence: num(r.confidence) })),
        prov: prov.map((r) => ({ hops: num(r.hops), confidence: num(r.confidence) })),
        moods: moods.map((r) => ({ p: num(r.p), a: num(r.a), d: num(r.d), bp: num(r.bp), ba: num(r.ba), bd: num(r.bd) })),
        minds: minds.map((r) => ({ joy: num(r.joy), anger: num(r.anger), fear: num(r.fear), hungerStreak: num(r.hungerStreak) })),
        emotions: emotions.map((r) => ({ etype: str(r.etype), intensity: num(r.intensity), halfLifeS: num(r.halfLifeS) })),
        aspirations: aspirations.map((r) => ({ kind: str(r.kind), progress: num(r.progress) })),
        opinions: opinions.map((r) => ({ lean: num(r.lean) })),
        intents: intents.map((r) => ({ activity: str(r.activity), commitment: num(r.commitment) })),
        faiths: faiths.map((r) => ({ faithId: str(r.faithId), intensity: num(r.intensity) })),
        priceBeliefs: priceBeliefs.map((r) => ({ commodity: str(r.commodity), mu: num(r.mu), sigma: num(r.sigma) })),
        truth: this.lastWorld?.rounds ?? [],
        kin: kin.map((r) => ({ kind: str(r.kind) })),
        playerRels: playerRels.map((r) => ({ versonaId: str(r.versonaId), nemesis: !!r.nemesis, rival: !!r.rival, seenCount: num(r.seenCount), talkStreak: num(r.talkStreak) })),
      };
      this.mindGroups = computeMindMetrics(m);
      this.lastMind = m;
      this.rollValues(this.mindGroups);
      this.refreshWatch();
      this.mindSourceCounts = {
        beliefs: beliefs.length, provenance: prov.length, moods: moods.length, emotions: emotions.length,
        aspirations: aspirations.length, opinions: opinions.length, intents: intents.length,
        faiths: faiths.length, priceBeliefs: priceBeliefs.length, kin: kin.length, playerRels: playerRels.length,
      };
      this.mindLoadedAt = performance.now();
      this.metricsGroups = [...this.metricsGroups.filter((g) => g.cat !== 'mind'), ...this.mindGroups];
      this.renderMetrics();
    } catch { /* the mind is optional — the tab stands without it */ } finally {
      this.mindLoading = false;
    }
  }

  /**
   * The two halves of the Commons, scoped here because only the module knows
   * which settlement flies which flag. Birth civ for people (`versona_origin`),
   * settlement civ for everything that happens in a place.
   */
  private civSides(input: MetricsInput, world: WorldMetricsInput): MetricGroup | null {
    const sides = new Map<string, CivSide>([['a', emptyCivSide('a')], ['b', emptyCivSide('b')]]);
    const civOfSid = (sid: string): CivSide | undefined => sides.get(input.civOfSettlement.get(sid) ?? '');
    for (const v of input.versonas) { const s = sides.get(input.originCiv.get(v.id) ?? ''); if (s) s.population++; }
    for (const civ of input.civOfSettlement.values()) { const s = sides.get(civ); if (s) s.settlements++; }
    for (const a of input.agents) { const s = civOfSid(a.settlementId); if (s) { s.agents++; s.wallet += a.wallet; } }
    for (const o of input.offices) { const s = civOfSid(o.settlementId); if (s) s.offices++; }
    for (const g of input.graves) { const s = civOfSid(g.settlementId); if (s) s.graves++; }
    for (const c of world.crimes) {
      const s = civOfSid(c.settlementId);
      if (!s) continue;
      s.crimesTotal++;
      if (c.status === 'open') s.crimesOpen++; else if (c.status === 'guilty') s.guilty++;
    }
    const scoreSum = new Map<string, { sum: number; n: number }>();
    for (const sc of input.society?.scores ?? []) {
      const civ = input.civOfSettlement.get(sc.settlementId);
      if (!civ || !sides.has(civ)) continue;
      const acc = scoreSum.get(civ) ?? { sum: 0, n: 0 };
      acc.sum += (sc.cohesion + sc.readiness + sc.learning + sc.health + sc.order + sc.piety + sc.prosperity) / 7;
      acc.n++;
      scoreSum.set(civ, acc);
    }
    for (const [civ, acc] of scoreSum) { const s = sides.get(civ); if (s && acc.n > 0) s.scoreMean = acc.sum / acc.n; }
    for (const f of input.society?.federations ?? []) {
      const s = sides.get(f.civ);
      if (!s) continue;
      s.purse = Number(f.purse); s.legitimacy = f.legitimacy; s.members = f.members;
      s.suspended = f.suspended; s.defiant = f.defiant; s.atWar = f.atWarWith !== '';
    }
    return compareCivs(sides.get('a')!, sides.get('b')!);
  }

  /** Cards in CHIP order, so "All" still reads as sections rather than a shuffle. */
  private orderedGroups(): MetricGroup[] {
    const rank = new Map(METRIC_CATS.map((c, i) => [c.id, i]));
    return [...this.metricsGroups].sort((x, y) => (rank.get(x.cat) ?? 99) - (rank.get(y.cat) ?? 99));
  }

  /**
   * THE DOOR (2026-09-20). Every card was a dead end: you could read "33 empty
   * guild halls" or "77 nemeses" and had no way to reach the rows behind the
   * number. A drill lists them, and a versona drill hands each one to the
   * roster — the Versonas tab, selected, dossier open.
   */
  private openDrill(d: Drill): void {
    this.drillPanel?.remove();
    const pane = this.paneMetrics;
    if (!pane) return;
    const box = document.createElement('div');
    this.drillPanel = box;
    box.id = 'vu-drill';
    box.style.cssText = 'position:absolute;right:14px;top:60px;width:300px;max-height:60%;overflow:auto;background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:8px 10px;z-index:30;box-shadow:0 6px 24px rgba(0,0,0,.5)';
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:6px';
    const title = document.createElement('div');
    title.textContent = `${d.label} · ${d.ids.length}${d.more ? ` of ${d.ids.length + d.more}` : ''}`;
    title.style.cssText = 'font-weight:600;color:var(--ax-hi);font-size:12px';
    const close = document.createElement('button');
    close.textContent = '\u00d7';
    close.style.cssText = 'cursor:pointer;background:none;border:none;color:var(--ax-soft);font-size:16px;line-height:1';
    close.onclick = (): void => { box.remove(); this.drillPanel = null; };
    head.append(title, close);
    box.appendChild(head);
    for (const id of d.ids) {
      const row = document.createElement('div');
      row.dataset.drillId = id;
      row.textContent = d.kind === 'versona' ? this.label(id) : id;
      row.style.cssText = `font-size:11.5px;color:${d.kind === 'versona' ? 'var(--ax-hi)' : 'var(--ax-text)'};padding:2px 0;cursor:${d.kind === 'versona' ? 'pointer' : 'default'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis`;
      if (d.kind === 'versona') {
        row.onclick = (): void => { this.showTab('versonas'); this.select(BigInt(id)); box.remove(); this.drillPanel = null; };
      }
      box.appendChild(row);
    }
    if (d.more) {
      const more = document.createElement('div');
      more.textContent = `\u2026 and ${d.more} more (capped at ${DRILL_CAP})`;
      more.style.cssText = 'font-size:10.5px;color:var(--ax-muted);padding-top:4px';
      box.appendChild(more);
    }
    pane.appendChild(box);
  }
  private drillPanel: HTMLDivElement | null = null;

  /**
   * EXPORT. The whole tab, flattened — cards, values, hints, bars and the watch
   * strip, plus the row counts each number was computed from, so a number can
   * be argued with later.
   */
  private exportMetrics(kind: 'json' | 'csv'): void {
    const groups = this.orderedGroups();
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let body: string, mime: string, ext: string;
    if (kind === 'json') {
      body = JSON.stringify({
        at: new Date().toISOString(),
        population: this.lastPopulation,
        sources: { ...this.metricsSourceCounts, ...this.worldSourceCounts, ...this.mindSourceCounts },
        watch: this.watch,
        groups: groups.map((g) => ({ id: g.id, cat: g.cat, title: g.title, metrics: g.metrics.map((m) => ({ label: m.label, value: m.value, hint: m.hint })), bars: g.bars ?? [] })),
      }, null, 2);
      mime = 'application/json'; ext = 'json';
    } else {
      const esc = (v: string): string => `"${String(v).replace(/"/g, '""')}"`;
      const lines = ['category,card,label,value,hint'];
      for (const g of groups) for (const m of g.metrics) lines.push([g.cat, g.title, m.label, m.value, m.hint ?? ''].map(esc).join(','));
      body = lines.join('\n');
      mime = 'text/csv'; ext = 'csv';
    }
    const url = URL.createObjectURL(new Blob([body], { type: mime }));
    const a2 = document.createElement('a');
    a2.href = url; a2.download = `versona-universe-metrics-${stamp}.${ext}`;
    a2.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  /** A pure number, or null when the value is compound ("2 of 3 · 67 %") and a delta would mislead. */
  private static pureNumber(v: string): number | null {
    return /^-?[\d,]+(\.\d+)?$/.test(v.trim()) ? Number(v.replace(/,/g, '')) : null;
  }

  private renderWatch(): void {
    const box = this.watchBox;
    if (!box) return;
    box.textContent = '';
    box.style.display = this.watch.length ? 'flex' : 'none';
    const tone: Record<string, [string, string, string]> = {
      alert: ['color-mix(in srgb, var(--ax-bad) 22%, var(--ax-bg2))', 'color-mix(in srgb, var(--ax-bad) 48%, var(--ax-line))', 'var(--ax-bad)'], warn: ['color-mix(in srgb, var(--ax-warn) 22%, var(--ax-bg2))', 'color-mix(in srgb, var(--ax-warn) 48%, var(--ax-line))', 'var(--ax-warn)'], note: ['var(--ax-bg3)', 'var(--ax-line2)', 'var(--ax-soft)'],
    };
    for (const wi of this.watch) {
      const [bg, br, fg] = tone[wi.level] ?? tone.note;
      const row = document.createElement('div');
      row.dataset.watch = wi.id;
      row.dataset.watchLevel = wi.level;
      row.title = wi.hint ?? '';
      row.style.cssText = `display:flex;gap:8px;align-items:center;background:${bg};border:1px solid ${br};border-radius:var(--ax-radius);padding:3px 8px;font-size:11.5px;color:${fg};cursor:pointer`;
      const tag = document.createElement('span');
      tag.textContent = wi.level.toUpperCase();
      tag.style.cssText = 'font-weight:700;font-size:9.5px;letter-spacing:.06em;opacity:.85;min-width:38px';
      const txt = document.createElement('span');
      txt.textContent = wi.text;
      row.append(tag, txt);
      // Clicking an alert lights the chip it belongs to — the strip is a door.
      row.onclick = (): void => { this.metricsCat = wi.cat; this.renderMetrics(); };
      box.appendChild(row);
    }
  }

  private renderMetrics(): void {
    const box = this.metricsBox;
    if (!box) return;
    box.textContent = '';
    this.paintChips();
    this.renderWatch();
    for (const g of this.orderedGroups()) {
      if (this.metricsCat !== 'all' && g.cat !== this.metricsCat) continue;
      const card = document.createElement('div');
      card.dataset.metricGroup = g.id;
      card.dataset.metricCat = g.cat;
      card.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:8px 10px;min-width:0';
      const h = document.createElement('div');
      h.textContent = g.title;
      h.style.cssText = 'font-weight:600;color:var(--ax-hi);margin-bottom:6px';
      card.appendChild(h);
      const table = document.createElement('table');
      table.style.cssText = 'width:100%;border-collapse:collapse;font-size:11.5px';
      for (const m of g.metrics) {
        const tr = document.createElement('tr');
        if (m.hint) tr.title = m.hint;
        const a = document.createElement('td');
        a.style.cssText = 'color:var(--ax-soft);padding:1px 6px 1px 0;white-space:nowrap';
        if (m.drill) {
          // A DOOR, not a label: the rows behind the number.
          a.textContent = `${m.label} ›`;
          a.style.cursor = 'pointer';
          a.style.color = 'var(--ax-hi)';
          a.dataset.drill = m.drill.label;
          a.onclick = (): void => { this.openDrill(m.drill!); };
        } else {
          a.textContent = m.label;
        }
        const b = document.createElement('td');
        b.style.cssText = 'color:var(--ax-text);text-align:right;padding:1px 0;font-variant-numeric:tabular-nums;white-space:nowrap';
        b.textContent = m.value;
        // WHAT CHANGED: only for values that are a plain number on BOTH sides —
        // a delta on "2 of 3 · 67 %" would be inventing arithmetic.
        const was = this.prevValues.get(`${g.id}|${m.label}`);
        if (was !== undefined && was !== m.value) {
          const n0 = VersonaUniverseModule.pureNumber(was), n1 = VersonaUniverseModule.pureNumber(m.value);
          if (n0 !== null && n1 !== null && n1 !== n0) {
            const d = n1 - n0;
            const span = document.createElement('span');
            span.textContent = ` ${d > 0 ? '+' : '\u2212'}${Math.abs(d).toLocaleString('en-US')}`;
            span.title = `was ${was}`;
            span.dataset.delta = String(d);
            span.style.cssText = `color:${d > 0 ? 'var(--ax-good)' : 'var(--ax-bad)'};font-size:10px`;
            b.appendChild(span);
          }
        }
        tr.append(a, b);
        table.appendChild(tr);
      }
      card.appendChild(table);
      // A TWO-COLUMN card (civ against civ): the whole point is reading the two
      // numbers on one line, so it replaces the bars rather than sitting beside them.
      if (g.pairs && g.pairs.length && g.columns) {
        const t2 = document.createElement('table');
        t2.style.cssText = 'width:100%;border-collapse:collapse;font-size:11.5px';
        const head = document.createElement('tr');
        for (const [i, h] of ['', g.columns[0], g.columns[1]].entries()) {
          const th = document.createElement('td');
          th.textContent = h;
          th.style.cssText = `color:var(--ax-muted);font-size:10px;letter-spacing:.04em;padding:0 0 3px ${i === 0 ? '0' : '6px'};text-align:${i === 0 ? 'left' : 'right'}`;
          head.appendChild(th);
        }
        t2.appendChild(head);
        for (const pr of g.pairs) {
          const tr = document.createElement('tr');
          if (pr.hint) tr.title = pr.hint;
          const l = document.createElement('td'); l.textContent = pr.label; l.style.cssText = 'color:var(--ax-soft);padding:1px 6px 1px 0;white-space:nowrap';
          // The bigger side is lit, so the reading is instant; a tie lights neither.
          const na = Number(pr.a.replace(/[^\d.-]/g, '')), nb = Number(pr.b.replace(/[^\d.-]/g, ''));
          const lead = Number.isFinite(na) && Number.isFinite(nb) && na !== nb ? (na > nb ? 'a' : 'b') : '';
          const ca = document.createElement('td'); ca.textContent = pr.a; ca.style.cssText = `color:${lead === 'a' ? 'var(--ax-good)' : 'var(--ax-text)'};text-align:right;padding:1px 0 1px 6px;font-variant-numeric:tabular-nums`;
          const cb = document.createElement('td'); cb.textContent = pr.b; cb.style.cssText = `color:${lead === 'b' ? 'var(--ax-good)' : 'var(--ax-text)'};text-align:right;padding:1px 0 1px 6px;font-variant-numeric:tabular-nums`;
          tr.append(l, ca, cb);
          t2.appendChild(tr);
        }
        card.appendChild(t2);
      }
      if (g.bars && g.bars.length) {
        const bars = document.createElement('div');
        bars.style.cssText = 'margin-top:6px;display:flex;flex-direction:column;gap:2px';
        const max = Math.max(1, ...g.bars.map((b) => b.value));
        for (const b of g.bars) {
          const row = document.createElement('div');
          row.style.cssText = 'display:grid;grid-template-columns:150px 1fr 56px;gap:6px;align-items:center;font-size:11px';
          const l = document.createElement('span'); l.textContent = b.label; l.style.cssText = 'color:var(--ax-soft);white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
          const track = document.createElement('div'); track.style.cssText = 'height:8px;background:var(--ax-bg3);border-radius:var(--ax-radius);overflow:hidden';
          const fill = document.createElement('div'); fill.style.cssText = `height:100%;width:${Math.round((b.value / max) * 100)}%;background:var(--ax-accent);border-radius:var(--ax-radius)`;
          track.appendChild(fill);
          const v = document.createElement('span'); v.textContent = `${b.value.toLocaleString('en-US')} · ${Math.round(b.share * 100)} %`; v.style.cssText = 'color:var(--ax-text);text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap';
          row.append(l, track, v);
          bars.appendChild(row);
        }
        card.appendChild(bars);
      }
      box.appendChild(card);
    }
    if (this.metricsStatus) this.metricsStatus.textContent = `${this.metricsGroups.length} groups · ${Object.entries(this.metricsSourceCounts).map(([k, v]) => `${k} ${v}`).join(' · ')}`;
  }

  // ─── charts tab ──────────────────────────────────────────────

  private buildCharts(): HTMLDivElement {
    const pane = document.createElement('div');
    pane.id = 'vu-charts';
    pane.style.cssText = 'display:none;flex:1;min-height:0;flex-direction:row';
    const side = document.createElement('div');
    side.style.cssText = 'width:250px;display:flex;flex-direction:column;border-right:1px solid var(--ax-line);min-width:200px';
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;flex-direction:column;gap:5px;padding:8px;border-bottom:1px solid var(--ax-line)';
    const sel = (id: string, title: string, opts: Array<[string, string]>, value: string): HTMLSelectElement => {
      const s = document.createElement('select');
      s.id = id; s.title = title;
      s.style.cssText = 'background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:2px 4px';
      for (const [v, l] of opts) { const o = document.createElement('option'); o.value = v; o.textContent = l; s.appendChild(o); }
      s.value = value;
      s.onchange = (): void => this.renderCharts();
      return s;
    };
    const labelled = (text: string, el: HTMLElement): HTMLDivElement => {
      const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:6px;align-items:center';
      const l = document.createElement('span'); l.style.cssText = 'width:70px;color:var(--ax-soft)'; l.textContent = text;
      el.style.flex = '1'; row.append(l, el); return row;
    };
    this.chartWindowSel = sel('vu-chart-window', 'time window', WINDOWS.map((w) => [w.label, w.label === 'all' ? 'all history' : `last ${w.label}`]), '24h');
    this.chartSiteSel = sel('vu-chart-site', 'settlement', [['', 'whole Commons']], '');
    this.chartCommoditySel = sel('vu-chart-commodity', 'commodity (economy series)', [['', 'all commodities']], '');
    this.chartModeSel = sel('vu-chart-mode', 'axis mode', [['scaled', 'scaled — each series 0–100 %'], ['raw', 'raw — one shared axis']], 'scaled');
    const reload = document.createElement('button');
    reload.id = 'vu-chart-reload';
    reload.textContent = 'Reload history';
    reload.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:2px 8px;font:11px var(--ax-font)';
    reload.onclick = (): void => { void this.loadChartData(true); };
    this.chartStatus = document.createElement('div');
    this.chartStatus.id = 'vu-chart-status';
    this.chartStatus.style.cssText = 'font-size:11px;color:var(--ax-soft);min-height:14px';
    controls.append(labelled('window', this.chartWindowSel), labelled('where', this.chartSiteSel), labelled('commodity', this.chartCommoditySel), labelled('axes', this.chartModeSel), reload, this.chartStatus);
    this.chartSeriesBox = document.createElement('div');
    this.chartSeriesBox.id = 'vu-chart-series';
    this.chartSeriesBox.style.cssText = 'flex:1;overflow:auto;padding:6px 8px';
    side.append(controls, this.chartSeriesBox);
    const main = document.createElement('div');
    main.style.cssText = 'flex:1;display:flex;flex-direction:column;min-width:0;padding:8px';
    this.chartCanvas = document.createElement('canvas');
    this.chartCanvas.id = 'vu-chart-canvas';
    this.chartCanvas.style.cssText = 'width:100%;height:420px;display:block;border:1px solid var(--ax-line);border-radius:var(--ax-radius);background:var(--ax-bg)';
    this.chartCanvas.onmousemove = (e): void => {
      const rect = this.chartCanvas!.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * this.chartCanvas!.width;
      const bins = this.chartComputed[0]?.values.length ?? 0;
      const L = 54, R = this.chartCanvas!.width - 14;
      const i = bins > 0 && x >= L && x <= R ? Math.min(bins - 1, Math.max(0, Math.floor(((x - L) / (R - L)) * bins))) : null;
      if (i !== this.chartHover) { this.chartHover = i; this.paintChart(); this.renderLegend(); }
    };
    this.chartCanvas.onmouseleave = (): void => { this.chartHover = null; this.paintChart(); this.renderLegend(); };
    this.chartLegend = document.createElement('div');
    this.chartLegend.id = 'vu-chart-legend';
    this.chartLegend.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px 16px;padding:8px 4px;font-size:11px';
    main.append(this.chartCanvas, this.chartLegend);
    pane.append(side, main);
    this.renderChartControls();
    return pane;
  }

  private renderChartControls(): void {
    const box = this.chartSeriesBox;
    if (!box) return;
    box.textContent = '';
    const groups: Array<[string, string]> = [['population', 'Population'], ['conflict', 'Conflicts'], ['economy', 'Economy'], ['society', 'Society']];
    for (const [g, label] of groups) {
      const h = document.createElement('div');
      h.style.cssText = 'margin:6px 0 2px;font:600 10px var(--ax-font);letter-spacing:.5px;color:var(--ax-hi);text-transform:uppercase';
      h.textContent = label;
      box.appendChild(h);
      for (const def of SERIES.filter((s) => s.group === g)) {
        const row = document.createElement('label');
        row.style.cssText = 'display:flex;gap:6px;align-items:center;cursor:pointer;padding:1px 0';
        const cb = document.createElement('input');
        cb.type = 'checkbox'; cb.checked = this.chartVisible.has(def.id); cb.dataset.series = def.id;
        cb.onchange = (): void => { if (cb.checked) this.chartVisible.add(def.id); else this.chartVisible.delete(def.id); this.renderCharts(); };
        const sw = document.createElement('span');
        sw.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:var(--ax-radius);background:${def.color}`;
        const t = document.createElement('span'); t.textContent = def.label;
        row.append(cb, sw, t);
        box.appendChild(row);
      }
    }
  }

  /** History over SQL: one statement per source, converted to ChartEvents. Cached; `force` re-fetches. */
  private async loadChartData(force: boolean): Promise<void> {
    if (this.chartLoading) return;
    if (!force && this.chartLoadedAt > 0 && performance.now() - this.chartLoadedAt < CHART_REFRESH_MS) return;
    this.chartLoading = true;
    this.chartError = '';
    if (this.chartStatus) this.chartStatus.textContent = 'loading history…';
    const ms = (v: unknown): number => Number((v as { microsSinceUnixEpoch?: bigint } | null)?.microsSinceUnixEpoch ?? 0n) / 1000;
    try {
      const [chron, graves, crimes, clashes, market, npcEv, wars, migrations] = await Promise.all([
        this.sql('SELECT * FROM chronicle'), this.sql('SELECT * FROM grave'), this.sql('SELECT * FROM crime_case'), this.sql('SELECT * FROM war_clash'),
        this.sql('SELECT * FROM market_round'), this.sql('SELECT * FROM npc_event'), this.sql('SELECT * FROM war_state'), this.sql('SELECT * FROM migration_log'),
      ]);
      const wid = str(atlas.commonsWorldId);
      const ev: ChartEvent[] = [];
      for (const r of chron) if (str(r.worldId) === wid) ev.push({ at: ms(r.at), source: 'chronicle', kind: str(r.kind), settlementId: str(r.settlementId) });
      for (const r of graves) if (str(r.worldId) === wid) ev.push({ at: ms(r.diedAt), source: 'grave', kind: str(r.cause), settlementId: str(r.settlementId) });
      for (const r of crimes) if (str(r.worldId) === wid) ev.push({ at: ms(r.openedAt), source: 'crime', kind: str(r.kind), settlementId: str(r.settlementId) });
      for (const r of clashes) if (str(r.worldId) === wid) ev.push({ at: ms(r.at), source: 'clash', kind: 'clash', settlementId: str(r.defenderSid) });
      for (const r of market) ev.push({ at: ms(r.at), source: 'market', kind: str(r.commodity), settlementId: str(r.settlementId), value: num(r.volume), price: num(r.avgPrice), unfilled: num(r.unfilled) });
      for (const r of npcEv) if (str(r.worldId) === wid) ev.push({ at: ms(r.at), source: 'npcEvent', kind: str(r.kind), settlementId: str(r.settlementId) });
      for (const r of wars) if (str(r.worldId) === wid) ev.push({ at: ms(r.startedAt), source: 'war', kind: str(r.status), settlementId: str(r.aggressorSid) });
      for (const r of migrations) if (str(r.worldId) === wid) ev.push({ at: ms(r.at), source: 'migration', kind: str(r.reason), settlementId: str(r.toSid) });
      ev.sort((a, b) => a.at - b.at);
      this.chartEvents = ev;
      this.chartSourceCounts = { chronicle: chron.length, grave: graves.length, crime: crimes.length, clash: clashes.length, market: market.length, npcEvent: npcEv.length, war: wars.length, migration: migrations.length };
      this.chartLoadedAt = performance.now();
      if (this.chartCommoditySel) {
        const cur = this.chartCommoditySel.value;
        const coms = [...new Set(market.map((r) => str(r.commodity)))].sort();
        this.chartCommoditySel.textContent = '';
        for (const [v, l] of [['', 'all commodities'], ...coms.map((c) => [c, c] as [string, string])]) { const o = document.createElement('option'); o.value = v; o.textContent = l; this.chartCommoditySel.appendChild(o); }
        this.chartCommoditySel.value = coms.includes(cur) ? cur : '';
      }
      if (this.chartSiteSel) {
        const cur = this.chartSiteSel.value;
        const sids = [...new Set(ev.map((e) => e.settlementId).filter((x) => x && x !== '0'))].sort((a, b) => Number(a) - Number(b));
        this.chartSiteSel.textContent = '';
        for (const [v, l] of [['', 'whole Commons'], ['civ:a', 'civ A — every Aurelian town'], ['civ:b', 'civ B — every Borean town'], ...sids.map((x) => [x, this.settlementName(x)] as [string, string])]) { const o = document.createElement('option'); o.value = v; o.textContent = l; this.chartSiteSel.appendChild(o); }
        this.chartSiteSel.value = sids.includes(cur) || cur.startsWith('civ:') ? cur : '';
      }
      this.renderCharts();
    } catch (e) {
      this.chartError = String((e as Error).message ?? e);
      if (this.chartStatus) this.chartStatus.textContent = `history failed: ${this.chartError}`;
    } finally { this.chartLoading = false; }
  }

  private chartWindow(): WindowDef { return WINDOWS.find((w) => w.label === this.chartWindowSel?.value) ?? WINDOWS[2]; }

  private renderCharts(): void {
    if (!this.chartCanvas || this.tabName !== 'charts') return;
    const win = this.chartWindow();
    const now = Date.now();
    const { t0, t1 } = windowRange(win, now, this.chartEvents);
    this.chartRange = { t0, t1 };
    const bins = defaultBins(win);
    const scope = this.chartSiteSel?.value || '';
    // A CIV scope (2026-09-18): the events of every settlement flying that civ, computed as one unscoped series.
    const civ = scope.startsWith('civ:') ? scope.slice(4) : null;
    const site = civ ? null : scope || null;
    const commodity = this.chartCommoditySel?.value || null;
    const inCiv = (sid: string): boolean => civ !== null && this.snap?.civ.get(sid) === civ;
    const events = civ ? this.chartEvents.filter((e) => inCiv(e.settlementId)) : this.chartEvents;
    const currentPopulation = site ? [...(this.snap?.agent.values() ?? [])].filter((a) => a.settlementId === site).length
      : civ ? [...(this.snap?.agent.values() ?? [])].filter((a) => inCiv(a.settlementId)).length : (this.snap?.versona.size ?? 0);
    const ctx = { now, settlementId: site, commodity, currentPopulation };
    this.chartComputed = SERIES.filter((s) => this.chartVisible.has(s.id)).map((def) => computeSeries(def, events, { t0, t1, bins, ctx }));
    if (this.chartStatus) this.chartStatus.textContent = this.chartLoadedAt ? `${this.chartEvents.length} events · ${bins} bins · ${Object.entries(this.chartSourceCounts).map(([k, v]) => `${k} ${v}`).join(' · ')}` : (this.chartError ? `history failed: ${this.chartError}` : 'loading history…');
    this.paintChart();
    this.renderLegend();
  }

  private paintChart(): void {
    const c = this.chartCanvas;
    if (!c) return;
    this.themeStale.delete('charts');
    const w = Math.max(320, Math.floor(c.clientWidth || 800)), h = 420;
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    drawChart(c, this.chartComputed, {
      t0: this.chartRange.t0, t1: this.chartRange.t1, mode: (this.chartModeSel?.value as 'scaled' | 'raw') ?? 'scaled', hoverIndex: this.chartHover,
      // the chart's chrome is the theme's (gaps wave 2026-09-28): it drew the old navy ground / grid / axis in every theme
      background: themeColor('--ax-bg'), grid: themeColor('--ax-line'), axis: themeColor('--ax-muted'), font: themeColor('--ax-font'),
      hover: themeColor('--ax-soft'), // a solid token: a color-mix() canvas stroke is unverified in Chromium
    });
  }

  private renderLegend(): void {
    const lg = this.chartLegend;
    if (!lg) return;
    lg.textContent = '';
    if (!this.chartComputed.length) { const d = document.createElement('div'); d.style.color = 'var(--ax-soft)'; d.textContent = 'Tick series on the left to plot them.'; lg.appendChild(d); return; }
    for (const s of this.chartComputed) {
      const item = document.createElement('div');
      item.style.cssText = 'display:flex;gap:6px;align-items:center';
      const sw = document.createElement('span'); sw.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:var(--ax-radius);background:${s.color}`;
      const t = document.createElement('span');
      const hv = this.chartHover !== null ? s.values[this.chartHover] ?? null : null;
      const delta = s.deltaPct === null ? '' : ` ${s.deltaPct >= 0 ? '▲' : '▼'} ${Math.abs(s.deltaPct).toFixed(0)}%`;
      t.textContent = this.chartHover !== null ? `${s.label}: ${formatValue(hv, s.unit)}` : `${s.label}: ${formatValue(s.first, s.unit)} → ${formatValue(s.last, s.unit)}${delta} (min ${formatValue(s.min, s.unit)} · max ${formatValue(s.max, s.unit)})`;
      t.style.color = s.deltaPct !== null && this.chartHover === null ? (s.deltaPct > 0 ? 'var(--ax-good)' : s.deltaPct < 0 ? 'var(--ax-bad)' : 'var(--ax-text)') : 'var(--ax-text)';
      item.append(sw, t);
      lg.appendChild(item);
    }
  }

  /** Probe: does the chart canvas carry non-background pixels? */
  private chartCanvasHasInk(): boolean {
    const c = this.chartCanvas;
    if (!c) return false;
    try {
      const ctx = c.getContext('2d');
      if (!ctx) return false;
      const d = ctx.getImageData(60, 20, Math.max(1, c.width - 80), Math.max(1, c.height - 60)).data;
      let ink = 0;
      for (let i = 0; i < d.length; i += 16) { const r = d[i], g = d[i + 1], b = d[i + 2]; if (Math.abs(r - 0x0b) + Math.abs(g - 0x11) + Math.abs(b - 0x19) > 60) ink++; }
      return ink > 50;
    } catch { return false; }
  }

  // ─── roster ──────────────────────────────────────────────────

  private fillSelect(sel: HTMLSelectElement, values: Array<[string, string]>, all: string): void {
    const cur = sel.value;
    const have = [...sel.options].map((o) => o.value).join('|');
    const want = ['', ...values.map((v) => v[0])].join('|');
    if (have === want) return;
    sel.textContent = '';
    const o0 = document.createElement('option'); o0.value = ''; o0.textContent = all; sel.appendChild(o0);
    for (const [v, label] of values) { const o = document.createElement('option'); o.value = v; o.textContent = label; sel.appendChild(o); }
    sel.value = [...sel.options].some((o) => o.value === cur) ? cur : '';
  }

  private renderList(): void {
    if (!this.list || !this.count || !this.search || !this.jobSel || !this.siteSel || !this.stateSel) return;
    const snap = this.snap;
    const all = this.roster();
    if (!snap) { this.count.textContent = this.snapError ? `snapshot failed: ${this.snapError}` : 'loading the roster…'; return; }
    // The filter selects follow the ROSTER, not the keystroke.
    if (this.rosterSelectsVersion !== this.rosterVersion) {
      const jobs = [...new Set(all.map((v) => snap.agent.get(v.id)?.job ?? v.archetype))].sort();
      const sids = [...new Set(all.map((v) => snap.agent.get(v.id)?.settlementId).filter((x): x is string => x !== undefined))].sort((a, b) => Number(a) - Number(b));
      const sts = [...new Set(all.map((v) => v.state))].sort();
      this.fillSelect(this.jobSel, jobs.map((j) => [j, j]), 'all jobs');
      this.fillSelect(this.siteSel, sids.map((x) => [x, this.settlementName(x)]), 'all settlements');
      this.fillSelect(this.stateSel, sts.map((x) => [x, x]), 'all states');
      this.rosterSelectsVersion = this.rosterVersion;
    }
    const q = this.search.value.trim().toLowerCase();
    const job = this.jobSel.value, sid = this.siteSel.value, st = this.stateSel.value;
    const shown = all.filter((v) => {
      const a = snap.agent.get(v.id);
      if (job && (a?.job ?? v.archetype) !== job) return false;
      if (sid && (a?.settlementId ?? '') !== sid) return false;
      if (st && v.state !== st) return false;
      if (q && !(this.rosterKey.get(v.id) ?? '').includes(q)) return false;
      return true;
    });
    // PAGED (2026-09-18: "only load the first 100 versonas … when you scroll down to the bottom it loads in more").
    const page = shown.slice(0, this.rosterShown);
    this.rosterShownCount = page.length;
    this.rosterMatched = shown.length;
    this.count.textContent = `${page.length < shown.length ? `${page.length} of ` : ''}${shown.length} shown · ${all.length} versonas · snapshot ${Math.round((performance.now() - snap.at) / 1000)}s ago`;
    const scrollTop = this.list.scrollTop;
    this.list.textContent = '';
    const frag = document.createDocumentFragment();
    for (const v of page) {
      const a = snap.agent.get(v.id);
      const row = document.createElement('div');
      row.dataset.versonaId = v.id;
      const sel = this.selected !== null && String(this.selected) === v.id;
      row.style.cssText = `padding:3px 8px;cursor:pointer;display:flex;gap:6px;align-items:baseline;${sel ? 'background:var(--ax-sel);color:var(--ax-text)' : ''}`;
      const name = document.createElement('span');
      name.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
      name.textContent = this.label(v.id);
      const meta = document.createElement('span');
      meta.style.cssText = 'font-size:10px;color:var(--ax-soft);white-space:nowrap';
      meta.textContent = `${v.state}${a ? ` · ${this.settlementName(a.settlementId).split(' ')[0]}` : ''}`;
      row.append(name, meta);
      row.onclick = (): void => this.select(BigInt(v.id));
      row.onmouseenter = (): void => { if (!sel) row.style.background = 'var(--ax-bg3)'; };
      row.onmouseleave = (): void => { if (!sel) row.style.background = ''; };
      frag.appendChild(row);
    }
    if (page.length < shown.length) {
      const more = document.createElement('div');
      more.id = 'vu-load-more';
      more.textContent = `${shown.length - page.length} more — scroll down, or click to load the next ${ROSTER_PAGE}`;
      more.style.cssText = 'padding:6px 8px;cursor:pointer;color:var(--ax-hi);text-align:center;font-size:11px;border-top:1px solid var(--ax-line)';
      more.onclick = (): void => this.loadMore();
      frag.appendChild(more);
    }
    this.list.appendChild(frag);
    this.list.scrollTop = scrollTop;
    if (!this.list.onscroll) this.list.onscroll = (): void => { const l = this.list; if (l && l.scrollTop + l.clientHeight >= l.scrollHeight - 120 && this.rosterShownCount < this.rosterMatched) this.loadMore(); };
  }

  private loadMore(): void { this.rosterShown += ROSTER_PAGE; this.renderList(); }
  private resetPage(): void { this.rosterShown = ROSTER_PAGE; this.renderList(); }

    private select(id: bigint): void {
    if (!this.snap?.versona.has(String(id))) { this.setNote(`no versona #${id} in the snapshot`, true); return; }
    this.selected = id;
    this.renderList();
    this.renderPane();
    void this.refreshRelations(); // the bonds pane needs the relation tables (fetched on demand, not on open)
    void this.refreshLive();
    void this.rebuildPreview();
  }

  // ─── right pane ──────────────────────────────────────────────

  private setNote(text: string, err = false): void {
    if (this.note) { this.note.textContent = text; this.note.style.color = err ? 'var(--ax-bad)' : 'var(--ax-good)'; }
  }

  private call(p: Promise<void> | undefined, done: string): void {
    if (!p) { this.setNote('not connected', true); return; }
    p.then(() => this.setNote(done)).catch((e: Error) => this.setNote(e.message, true));
  }

  private btn(label: string, tip: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label; b.title = tip;
    b.style.cssText = 'cursor:pointer;background:var(--ax-bg3);color:var(--ax-hi);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:1px 8px;font-size:11px';
    b.onclick = onClick;
    return b;
  }

  // ─── architecture tab (telemetry: how a versona thinks + acts) ─────────────

  private archBodies = new Map<string, HTMLDivElement>();

  /** The diagram is laid out ONCE (nodes + SVG arrows); renderArch() fills the
   *  per-node telemetry bodies from the selected versona's live poll. Flow:
   *  perception → memory/rumors → the self (personality/affect/needs/bonds) →
   *  deliberation (± the LLM) → decision (intent → goal) → action, with a
   *  parallel civic (opinion/voting) output. */
  private buildArch(): HTMLDivElement {
    const pane = document.createElement('div');
    pane.id = 'vu-arch';
    pane.style.cssText = 'display:none;flex:1;min-height:0;overflow:auto;background:var(--ax-bg);position:relative';
    const stage = document.createElement('div');
    stage.style.cssText = 'position:relative;width:1140px;height:660px;margin:0 auto';
    pane.appendChild(stage);
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', '1140'); svg.setAttribute('height', '660');
    svg.style.cssText = 'position:absolute;inset:0;pointer-events:none';
    const defs = document.createElementNS(NS, 'defs');
    const mk = document.createElementNS(NS, 'marker');
    mk.setAttribute('id', 'vu-arrow'); mk.setAttribute('viewBox', '0 0 10 10'); mk.setAttribute('refX', '9'); mk.setAttribute('refY', '5');
    mk.setAttribute('markerWidth', '6'); mk.setAttribute('markerHeight', '6'); mk.setAttribute('orient', 'auto-start-reverse');
    const tri = document.createElementNS(NS, 'path'); tri.setAttribute('d', 'M0,0 L10,5 L0,10 z'); tri.style.fill = 'var(--ax-line2)'; // chrome: the theme's line (an SVG attribute cannot take var())
    mk.appendChild(tri); defs.appendChild(mk); svg.appendChild(defs);
    stage.appendChild(svg);

    interface NodeDef { id: string; x: number; y: number; title: string; color: string }
    const W = 210, H = 108;
    const nodes: NodeDef[] = [
      { id: 'perception', x: 12, y: 20, title: '👁  Observation', color: '#2b6cb0' },
      { id: 'needs', x: 12, y: 160, title: '🍞  Needs', color: '#2b6cb0' },
      { id: 'personality', x: 12, y: 300, title: '🎭  Personality', color: '#2b6cb0' },
      { id: 'relationships', x: 12, y: 440, title: '🤝  Relationships', color: '#2b6cb0' },
      { id: 'memory', x: 300, y: 20, title: '🧠  Memory', color: '#6b46c1' },
      { id: 'rumors', x: 300, y: 160, title: '💬  Rumors (hearsay)', color: '#6b46c1' },
      { id: 'affect', x: 300, y: 300, title: '❤️  Affect · Mood', color: '#6b46c1' },
      { id: 'mind', x: 590, y: 120, title: '⚙️  Deliberation (mind)', color: '#c05621' },
      { id: 'llm', x: 590, y: 320, title: '🗣  Brain · LLM', color: '#c05621' },
      { id: 'intent', x: 880, y: 20, title: '➡️  Intent', color: '#2f855a' },
      { id: 'goal', x: 880, y: 160, title: '🎯  Goal', color: '#2f855a' },
      { id: 'action', x: 880, y: 300, title: '🏃  Action · State', color: '#2f855a' },
      { id: 'civic', x: 880, y: 440, title: '🏛  Civic · Voting', color: '#2f855a' },
    ];
    const pos = new Map<string, NodeDef>();
    for (const nd of nodes) pos.set(nd.id, nd);
    const edges: Array<[string, string]> = [
      ['perception', 'memory'], ['memory', 'rumors'], ['perception', 'affect'],
      ['perception', 'mind'], ['memory', 'mind'], ['rumors', 'mind'],
      ['personality', 'mind'], ['affect', 'mind'], ['needs', 'mind'], ['relationships', 'mind'],
      ['affect', 'personality'], ['mind', 'llm'], ['llm', 'mind'],
      ['mind', 'intent'], ['intent', 'goal'], ['goal', 'action'], ['mind', 'civic'],
    ];
    const anchor2 = (n: NodeDef, side: 'l' | 'r' | 't' | 'b'): [number, number] =>
      side === 'r' ? [n.x + W, n.y + H / 2] : side === 'l' ? [n.x, n.y + H / 2] : side === 't' ? [n.x + W / 2, n.y] : [n.x + W / 2, n.y + H];
    for (const [a, b] of edges) {
      const na = pos.get(a)!, nb = pos.get(b)!;
      // pick sides by relative position
      let sa: 'l' | 'r' | 't' | 'b' = 'r', sb: 'l' | 'r' | 't' | 'b' = 'l';
      if (Math.abs(nb.x - na.x) < W) { sa = nb.y > na.y ? 'b' : 't'; sb = nb.y > na.y ? 't' : 'b'; }
      else if (nb.x < na.x) { sa = 'l'; sb = 'r'; }
      const [x1, y1] = anchor2(na, sa); const [x2, y2] = anchor2(nb, sb);
      const path = document.createElementNS(NS, 'path');
      const mx = (x1 + x2) / 2;
      const d = (sa === 'r' || sa === 'l') ? `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}` : `M${x1},${y1} C${x1},${(y1 + y2) / 2} ${x2},${(y1 + y2) / 2} ${x2},${y2}`;
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      if (a === 'llm' || b === 'llm') path.setAttribute('stroke', '#8a6d3b'); // the LLM link keeps its category colour
      else path.style.stroke = 'var(--ax-line2)'; // gaps wave (2026-09-28): a plain edge is chrome — it was the navy UI's line
      path.setAttribute('stroke-width', '1.6');
      path.setAttribute('marker-end', 'url(#vu-arrow)');
      if (a === 'affect' && b === 'personality') path.setAttribute('stroke-dasharray', '4 3');
      svg.appendChild(path);
    }
    for (const nd of nodes) {
      const box = document.createElement('div');
      box.style.cssText = `position:absolute;left:${nd.x}px;top:${nd.y}px;width:${W}px;min-height:${H}px;` +
        `background:var(--ax-bg2);border:1px solid ${nd.color};border-radius:var(--ax-radius);box-shadow:0 2px 10px rgba(0,0,0,.5);overflow:hidden`;
      const hd = document.createElement('div');
      hd.textContent = nd.title;
      hd.style.cssText = `background:${nd.color}22;color:var(--ax-text);font:700 12px var(--ax-font);padding:5px 8px;border-bottom:1px solid ${nd.color}55`;
      const body = document.createElement('div');
      body.id = `arch-body-${nd.id}`;
      body.style.cssText = 'padding:6px 8px;font:11px/1.45 var(--ax-mono);color:var(--ax-text);white-space:pre-wrap';
      body.textContent = '—';
      this.archBodies.set(nd.id, body);
      box.append(hd, body);
      stage.appendChild(box);
    }
    // Legend + title.
    const cap = document.createElement('div');
    cap.style.cssText = 'position:absolute;left:12px;top:560px;width:520px;color:var(--ax-muted);font:11px/1.5 var(--ax-font)';
    cap.innerHTML = 'Select a versona on the <b>Versonas</b> tab, then return here — every node fills with that versona’s live telemetry (2.5&nbsp;s poll). Solid arrows are the think→act flow; the dashed arrow is affect feeding back into temperament.';
    stage.appendChild(cap);
    return pane;
  }

  private renderArch(): void {
    if (!this.paneArch) return;
    const set = (id: string, text: string): void => { const b = this.archBodies.get(id); if (b) b.textContent = text; };
    if (this.selected === null) { for (const id of this.archBodies.keys()) set(id, '(no versona selected)'); return; }
    const idS = String(this.selected);
    const rows = (t: string): Record<string, unknown>[] => this.liveRows(t) as Record<string, unknown>[];
    const one = (t: string): Record<string, unknown> | undefined => rows(t)[0];
    const num = (v: unknown): number => typeof v === 'number' ? v : typeof v === 'bigint' ? Number(v) : Number(v ?? 0);
    const str = (v: unknown): string => v == null ? '—' : String(v);
    const micros = (r: Record<string, unknown>, k: string): number => { const v = r[k] as { microsSinceUnixEpoch?: bigint } | number | undefined; return typeof v === 'object' && v?.microsSinceUnixEpoch != null ? Number(v.microsSinceUnixEpoch) : num(v); };
    const latest = (list: Record<string, unknown>[], k: string): Record<string, unknown> | undefined => list.length ? [...list].sort((a, b) => micros(b, k) - micros(a, k))[0] : undefined;
    const clip = (v: unknown, n = 42): string => { const t = str(v); return t.length > n ? t.slice(0, n - 1) + '…' : t; };

    const agent = this.snap?.agent.get(idS);
    const vsn = this.snap?.versona.get(idS);

    // OBSERVATION
    const ev = rows('npcEvent'); const st = one('versonaState');
    set('perception', `state: ${str(st?.state)}\nevents seen: ${ev.length}\n${ev.length ? 'last: ' + clip(latest(ev, 'at')?.text ?? latest(ev, 'at')?.kind) : '—'}`);
    // NEEDS
    set('needs', agent ? `hunger:  ${num(agent.hunger)}\nrest:    ${num(agent.needRest)}\nsocial:  ${num(agent.needSocial)}\nwallet:  ${num(agent.wallet)}` : '—');
    // PERSONALITY
    const mind = one('npcMind');
    set('personality', `archetype: ${str(vsn?.archetype)}\njoy ${num(mind?.joy)} · anger ${num(mind?.anger)} · fear ${num(mind?.fear)}\nhungerStreak: ${num(mind?.hungerStreak)}`);
    // RELATIONSHIPS
    const grud = rows('versonaGrudge'); const worst = grud.length ? [...grud].sort((a, b) => num(b.intensity) - num(a.intensity))[0] : undefined;
    set('relationships', `bonds: ${rows('versonaPair').length} · favors: ${rows('versonaFavor').length}\ngrudges: ${grud.length}${worst ? '\nworst: ' + clip(worst.causeText, 34) + ' (' + num(worst.intensity) + ')' : ''}`);
    // MEMORY (witness + reflection)
    const mem = rows('versonaMemory');
    const witness = mem.filter((m) => m.kind === 'witness'); const refl = mem.filter((m) => m.kind === 'reflection');
    const lm = latest(mem.filter((m) => m.kind !== 'rumor'), 'at');
    // Cognition wave: the SEMANTIC layer (beliefs) + last night's diary line.
    const beliefs = rows('versonaBelief');
    const diary = beliefs.filter((b) => b.kind === 'reflection').sort((a, b) => (num(b.beliefId) - num(a.beliefId)))[0];
    set('memory', `total: ${mem.length}  (witness ${witness.length} · reflection ${refl.length})\nbeliefs: ${beliefs.length}${diary ? '\n📖 ' + clip(diary.text, 40) : ''}\n${lm ? 'last: ' + clip(lm.text) + '\nimportance ' + num(lm.importance) : '—'}`);
    // RUMORS
    const rum = mem.filter((m) => m.kind === 'rumor'); const lr = latest(rum, 'at');
    set('rumors', `heard: ${rum.length}\n${lr ? clip(lr.text) + '\nimportance ' + num(lr.importance) : 'none'}`);
    // AFFECT · MOOD
    const mood = one('npcMood'); const emo = rows('versonaEmotion'); const topE = emo.length ? [...emo].sort((a, b) => num(b.intensity) - num(a.intensity))[0] : undefined;
    set('affect', mood ? `PAD  P ${(num(mood.pleasureX10) / 10).toFixed(1)} · A ${(num(mood.arousalX10) / 10).toFixed(1)} · D ${(num(mood.dominanceX10) / 10).toFixed(1)}\nepisodes: ${emo.length}${topE ? '\ntop: ' + str(topE.etype) + ' ' + num(topE.intensity) + (topE.causeText ? ' — ' + clip(topE.causeText, 24) : '') : ''}` : `episodes: ${emo.length}`);
    // DELIBERATION (mind)
    const delib = rows('versonaDeliberation'); const ld = latest(delib, 'at') ?? delib[0];
    const thought = one('versonaThought');
    set('mind', `deliberations: ${delib.length}\n${thought ? '💭 ' + clip(thought.text, 40) + '\n' : ''}${ld ? 'kind: ' + str(ld.kind) + '\nstatus: ' + str(ld.status) : 'idle — routine behavior'}`);
    // LLM
    const chat = rows('versonaChat'); const answered = chat.filter((c) => c.answered === true); const lc = latest(chat, 'at');
    const llmState = answered.length ? 'CONNECTED (worker replied)' : chat.length ? 'awaiting reply' : 'idle (no free-text)';
    set('llm', `${llmState}\nexchanges: ${chat.length} · answered: ${answered.length}\n${lc ? clip(str(lc.speaker) + ': ' + str(lc.text), 44) : '—'}`);
    // INTENT
    const it = one('versonaIntent');
    const asp = one('versonaAspiration');
    set('intent', `${it ? 'activity: ' + str(it.activity) + '\ncommitment: ' + num(it.commitment) : '—'}${asp ? '\n⭐ dream: ' + str(asp.kind) + ' (' + num(asp.progress) + '%)' : ''}`);
    // GOAL
    const gl = one('versonaGoal');
    set('goal', gl ? `state: ${str(gl.state)}\ntarget: (${Math.round(num(gl.x))}, ${Math.round(num(gl.z))})` : '—');
    // ACTION / STATE
    set('action', `now: ${str(st?.state)}\ncell: (${Math.round(num(st?.curX))}, ${Math.round(num(st?.curZ))})`);
    // CIVIC / VOTING
    const op = one('versonaOpinion'); const civicDelib = delib.filter((d) => ['amendment', 'norm', 'speech', 'goal', 'crisis'].includes(String(d.kind)));
    set('civic', `political lean: ${op ? (num(op.leanX10) / 10).toFixed(1) : '—'}\ncivic deliberations: ${civicDelib.length}`);
  }

  private renderPane(): void {
    if (this.tabName === 'architecture') this.renderArch();
    if (!this.nowPane || !this.dossier) return;
    // Never clobber a field being typed into (the dossier's editors re-render each second).
    const ae = document.activeElement;
    if (ae && (this.dossier.contains(ae) || this.nowPane.contains(ae)) && (ae.tagName === 'INPUT' || ae.tagName === 'SELECT' || ae.tagName === 'TEXTAREA')) return;
    const id = this.selected === null ? null : String(this.selected);
    this.nowPane.textContent = '';
    if (id === null) {
      this.dossier.textContent = '';
      const p = document.createElement('div');
      p.style.cssText = 'color:var(--ax-soft);padding:12px 0';
      p.textContent = 'Select a versona on the left.';
      this.nowPane.appendChild(p);
      if (this.caption) this.caption.textContent = '';
      return;
    }
    const idN = BigInt(id);
    const r = this.reducers();
    const snap = this.snap;
    const agent = snap?.agent.get(id);
    const where = this.whereIs(id);
    const intent = (this.liveRows('versonaIntent') as IntentRow[]).find((x) => x.versonaId === idN);
    const goal = (this.liveRows('versonaGoal') as GoalRow[]).find((x) => x.versonaId === idN);
    const enc = (this.liveRows('versonaEncounter') as EncounterRow[]).find((x) => x.aId === idN || x.bId === idN);
    const state = (this.liveRows('versonaState') as StateRow[]).find((s) => s.versonaId === idN)?.state ?? snap?.versona.get(id)?.state ?? '?';
    const h = document.createElement('div');
    h.style.cssText = 'font:600 15px var(--ax-font);color:var(--ax-text);margin-bottom:4px';
    h.textContent = this.fullLabel(id);
    this.nowPane.appendChild(h);
    const line = (text: string, dim = false, onClick?: () => void): void => {
      const d = document.createElement('div');
      d.style.cssText = `margin:1px 0;${dim ? 'opacity:.7' : ''}${onClick ? ';cursor:pointer;color:var(--ax-hi)' : ''}`;
      d.textContent = text;
      if (onClick) d.onclick = onClick;
      this.nowPane!.appendChild(d);
    };
    const head = (text: string): void => {
      const d = document.createElement('div');
      d.style.cssText = 'margin:8px 0 2px;font:600 11px var(--ax-font);letter-spacing:.5px;color:var(--ax-hi);text-transform:uppercase';
      d.textContent = text;
      this.nowPane!.appendChild(d);
    };
    const jump = (other: string): (() => void) => () => this.select(BigInt(other));
    head('Now');
    line(`state: ${state}${intent ? ` · doing: ${intent.activity} (${['open', 'single', 'blind'][intent.commitment] ?? intent.commitment})` : ' · doing: (no intent row yet)'}`);
    if (goal) line(`goal: → (${goal.x.toFixed(0)}, ${goal.z.toFixed(0)}) ${goal.state}`);
    if (enc) { const other = String(enc.aId === idN ? enc.bId : enc.aId); line(`encounter: ${enc.kind} ${enc.state} with ${this.label(other)}`, false, jump(other)); }
    if (agent) line(`needs: hunger ${agent.hunger} · rest ${agent.needRest} · social ${agent.needSocial} · mood ${agent.mood} · wallet ${agent.wallet}ⓒ`, true);
    head('Where');
    if (where) {
      const d = Math.hypot(where.x - atlas.localX, where.z - atlas.localZ);
      const fmt = (m: number): string => (m < 1000 ? `${m.toFixed(0)} m` : `${(m / 1000).toFixed(1)} km`);
      const site = agent ? this.siteOf(agent.settlementId) : null;
      const fromHome = site ? Math.hypot(where.x - site.centerX, where.z - site.centerZ) : null;
      line(`at (${where.x.toFixed(0)}, ${where.z.toFixed(0)}) — ${fmt(d)} from you${where.hot ? ' (live)' : ' (last known — hot row not in yet)'}`);
      line(`home: ${agent ? this.settlementName(agent.settlementId) : 'unknown settlement'}${fromHome !== null ? ` (${fmt(fromHome)} from its centre)` : ''}`, true);
    } else line('position unknown', true);
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:5px;flex-wrap:wrap;margin:4px 0';
    row.appendChild(this.btn('teleport to them', 'move yourself to this versona (debugTeleportToVersona)', () => this.call(r?.debugTeleportToVersona({ versonaId: idN }), 'teleported')));
    row.appendChild(this.btn('bring here', 'goal = your position for 30 s', () => this.call(r?.setVersonaGoal({ versonaId: idN, x: atlas.localX, z: atlas.localZ, state: 'working', ttlSeconds: 30 }), 'summoned')));
    this.nowPane.appendChild(row);
    // ── relationships from the snapshot (names; click to jump) ──
    head('Relationships');
    if (snap) {
      if (!snap.relationsAt) line(this.relationsLoading ? 'bonds, favors and grudges loading…' : 'bonds not loaded yet', true);
      const mine = snap.lineage.find((l) => l.childId === id);
      const kids = snap.lineage.filter((l) => l.parentA === id || l.parentB === id);
      if (mine) { line(`parents: ${this.label(mine.parentA)} · ${this.label(mine.parentB)} — generation ${mine.generation}`, false, jump(mine.parentA)); }
      for (const k of kids) line(`child: ${this.label(k.childId)}`, false, jump(k.childId));
      const pairs = snap.pairs.filter((p) => p.aId === id || p.bId === id).sort((x, y) => y.familiarity - x.familiarity);
      for (const p of pairs.slice(0, 12)) { const other = p.aId === id ? p.bId : p.aId; line(`bond: ${this.label(other)} — familiarity ${p.familiarity}`, false, jump(other)); }
      if (pairs.length > 12) line(`… ${pairs.length - 12} more bonds`, true);
      for (const f of snap.favors.filter((f) => f.aId === id || f.bId === id)) { const other = f.aId === id ? f.bId : f.aId; const owed = f.aId === id ? f.balance : -f.balance; line(`favor: ${owed > 0 ? `${this.label(other)} owes them ${owed}ⓒ` : `they owe ${this.label(other)} ${-owed}ⓒ`}`, false, jump(other)); }
      for (const g of snap.grudges.filter((g) => g.versonaId === id)) line(`grudge vs ${this.label(g.targetId)} — ${g.intensity}: ${g.causeText}`, false, jump(g.targetId));
      for (const g of snap.grudges.filter((g) => g.targetId === id)) line(`grudge held by ${this.label(g.versonaId)} — ${g.intensity}: ${g.causeText}`, false, jump(g.versonaId));
      if (!mine && !kids.length && !pairs.length) line('(no family or bonds yet)', true);
    } else line('(snapshot loading)', true);
    head('Dossier');
    // The inspector renders the rest — stats, affect, grudges, memories (editable), intent, interactions, scenarios — from the scoped live rows.
    if (this.liveFor !== id) { const w = document.createElement('div'); w.style.cssText = 'color:var(--ax-soft)'; w.textContent = 'polling live rows…'; this.dossier.textContent = ''; this.dossier.appendChild(w); return; }
    this.inspector.renderInto(this.dossier, idN, { subscribe: false, rows: (n) => this.liveRows(n), reducers: this.http }); // rows from the SQL poll, edits over HTTP
  }

  // ─── 3D preview ──────────────────────────────────────────────

  private async startPreview(): Promise<void> {
    if (this.pvRenderer) { this.spin(); return; }
    if (this.pvStarting || !this.canvas) return;
    this.pvStarting = true;
    try {
      const canvas = this.canvas;
      const r = new THREE.WebGPURenderer({ canvas, antialias: false, forceWebGL: true, alpha: true });
      await r.init();
      const w = Math.max(2, canvas.clientWidth), h = Math.max(2, canvas.clientHeight);
      r.setSize(w, h, false);
      r.setPixelRatio(1);
      r.toneMapping = THREE.ACESFilmicToneMapping;
      r.toneMappingExposure = 1.1;
      r.setClearColor(0x000000, 0);
      this.pvRenderer = r;
      const scene = new THREE.Scene();
      scene.add(new THREE.HemisphereLight(0xcfe0ff, 0x2a2536, 1.5));
      const key = new THREE.DirectionalLight(0xfff1e0, 2.6); key.position.set(1.8, 2.6, 2.2); scene.add(key);
      const rim = new THREE.DirectionalLight(0x8fb8ff, 1.6); rim.position.set(-2.2, 1.8, -2.4); scene.add(rim);
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.7, 0.76, 64), new THREE.MeshBasicMaterial({ color: 0x6fa8ff, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false }));
      ring.rotation.x = -Math.PI / 2; ring.position.y = 0.01; scene.add(ring);
      this.pvScene = scene;
      this.pvCamera = new THREE.PerspectiveCamera(32, w / h, 0.05, 20);
      this.pvCamera.position.set(0, 1.1, 3.4);
      this.pvCamera.lookAt(0, 0.9, 0);
      this.canvas.onpointerenter = (): void => { this.pvHover = true; this.resumePreview(); };
      this.canvas.onpointerleave = (): void => { this.pvHover = false; };
      await this.rebuildPreview();
      this.spin();
    } catch {
      this.pvStarting = false; // headless / no GL — the dossier stands alone
    }
  }

  /**
   * ON-DEMAND rendering: a free-running RAF loop on a second GL context starved the
   * tab (subscriptions never applied while the turntable spun). The body spins for
   * PREVIEW_SPIN_MS after a selection and while the pointer hovers the canvas;
   * otherwise one frame is drawn and the loop stops.
   */
  private spin(ms = PREVIEW_SPIN_MS): void { this.pvSpinUntil = Math.max(this.pvSpinUntil, performance.now() + ms); this.resumePreview(); }
  private resumePreview(): void {
    if (this.pvRaf || !this.pvRenderer) return;
    const loop = (): void => {
      if (!this.win?.isOpen() || !this.pvRenderer || !this.pvScene || !this.pvCamera) { this.pvRaf = 0; return; }
      const spinning = this.pvHover || performance.now() < this.pvSpinUntil;
      if (this.pvBody && spinning) this.pvBody.rotation.y += 0.02;
      void this.pvRenderer.render(this.pvScene, this.pvCamera);
      // rAF ids and timer ids are separate spaces: cancelling a timer id with
      // cancelAnimationFrame could kill the engine's own render loop.
      this.pvRaf = 0;
      this.pvTimer = spinning ? window.setTimeout(() => { this.pvTimer = 0; this.pvRaf = requestAnimationFrame(loop); }, 1000 / PREVIEW_FPS) : 0;
    };
    this.pvRaf = requestAnimationFrame(loop);
  }
  private pausePreview(): void {
    if (this.pvRaf) { cancelAnimationFrame(this.pvRaf); this.pvRaf = 0; }
    if (this.pvTimer) { window.clearTimeout(this.pvTimer); this.pvTimer = 0; }
  }

  /** Remove the current preview body and hand back its asset ref (or dispose the fallback capsule). */
  private releasePreviewBody(): void {
    if (this.pvBody) {
      this.pvScene?.remove(this.pvBody);
      if (this.pvAssetId !== null) { this.assets.dispose(this.pvAssetId); this.pvAssetId = null; }
      else this.pvBody.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); } });
      this.pvBody = null;
    }
  }

  /** The body the crowd renderer would give this versona: archetype pool (or its civilization's citizen body). */
  private avatarKeyFor(id: string): string {
    const v = this.snap?.versona.get(id);
    const a = this.snap?.agent.get(id);
    const civ = a ? this.snap?.civ.get(a.settlementId) ?? null : null;
    // a wave-4 body this shard never ingested shows as the pre-wave body the crowd stands in — never the capsule
    const reg = new Set(this.tbl<PrefabRow>('prefabModel').map((p) => p.prefabKey));
    return shownAvatarFor(v?.archetype ?? 'civilian', BigInt(id), civ, (k) => reg.has(k));
  }

  private async rebuildPreview(): Promise<void> {
    if (!this.pvScene || this.selected === null) return;
    const id = this.selected;
    const key = this.avatarKeyFor(String(id));
    if (key === this.pvKey && this.pvBody) return;
    const assetId = this.tbl<PrefabRow>('prefabModel').find((p) => p.prefabKey === key)?.assetId;
    let body: THREE.Object3D;
    let heldId: bigint | null = null;
    try {
      if (assetId === undefined) throw new Error('no prefab');
      body = (await this.assets.load(assetId)).clone(); // the cache hands back a SHARED object — always clone
      heldId = assetId; // the load is a cache ref; release it when this body is swapped out
    } catch {
      body = new THREE.Mesh(new THREE.CapsuleGeometry(0.32, 1.1, 6, 12), new THREE.MeshStandardMaterial({ color: 0x6f87b0, roughness: 0.8 }));
      body.position.y = 0.87;
    }
    if (this.selected !== id) { if (heldId !== null) this.assets.dispose(heldId); return; } // selection moved on while loading
    this.releasePreviewBody();
    this.pvAssetId = heldId;
    const box = new THREE.Box3().setFromObject(body);
    const size = box.getSize(new THREE.Vector3());
    const s = size.y > 0.01 ? 1.8 / size.y : 1;
    body.scale.multiplyScalar(s);
    const box2 = new THREE.Box3().setFromObject(body);
    const c = box2.getCenter(new THREE.Vector3());
    body.position.x -= c.x; body.position.z -= c.z; body.position.y -= box2.min.y;
    this.pvBody = body;
    this.pvKey = key;
    this.pvScene.add(body);
    if (this.caption) this.caption.textContent = `${this.fullLabel(String(id))}\n${key}`;
    this.spin();
  }

  private previewStartTimer: number | null = null;
  dispose(): void {
    if (this.previewStartTimer !== null) { window.clearTimeout(this.previewStartTimer); this.previewStartTimer = null; }
    if (this.snapTimer !== null) window.clearInterval(this.snapTimer);
    if (this.paneTimer !== null) window.clearInterval(this.paneTimer);
    if (this.liveTimer !== null) window.clearInterval(this.liveTimer);
    if (this.chartTimer !== null) window.clearInterval(this.chartTimer);
    if (this.metricsTimer !== null) window.clearInterval(this.metricsTimer);
    this.pausePreview();
    this.releasePreviewBody();
    this.pvRenderer?.dispose();
  }
}
